import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { X, Save, Plus, Trash2, FileText, Package, Shield, AlertTriangle, Settings, Loader2, Receipt, CreditCard, Search } from "lucide-react";
import { toast } from "sonner";
import ProductoSearchSelect from "./ProductoSearchSelect";
import MoneyInput from "./ui/MoneyInput";
import { EntidadModal } from "../pages/CatastroPage";
import MedioCobroSelector, { useProcesadoras, SIN_MEDIO } from "./MedioCobroSelector";
import RecetaFormModal from "./RecetaFormModal";
import { imprimirRecetaDeFactura, verificarControlados } from "../lib/recetaPrint";
import DateInput from "./ui/DateInput";
import useDepositos from "../hooks/useDepositos";

// Buscador de cliente con creación inline en el Catastro — mismo patrón que
// ProveedorSearchSelect en Compras (EgresosPage.jsx), adaptado a clientes.
export const ClienteSearchSelect = ({ empresas, value, onSelect, onCreated, token, logo }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [clienteModalNombre, setClienteModalNombre] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return (empresas || []).slice(0, 12);
    const q = query.toLowerCase();
    return (empresas || []).filter(e =>
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.razon_social || "").toLowerCase().includes(q) ||
      (e.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [empresas, query]);

  const exactMatch = (empresas || []).some(e => (e.nombre || e.razon_social || "").toLowerCase() === query.trim().toLowerCase());

  const handlePick = (emp) => {
    onSelect(emp);
    setQuery(""); setOpen(false); setClienteModalNombre("");
  };

  if (value?.id) {
    return (
      <div className="flex items-center gap-2 bg-white border border-blue-300 rounded-lg px-3 py-2 min-h-[38px]">
        <div className="flex-1 min-w-0">
          <span className="text-gray-800 text-sm font-medium truncate block">{value.nombre || value.razon_social}</span>
          {value.ruc && <span className="text-gray-400 text-xs">RUC: {value.ruc}</span>}
        </div>
        <button type="button" onClick={() => onSelect(null)} className="text-gray-400 hover:text-gray-700 shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar cliente por nombre o RUC..."
          className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-gray-800 text-sm focus:outline-none focus:border-blue-400 placeholder:text-gray-400"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 && (
            <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
              {filtered.map(e => (
                <li key={e.id} onMouseDown={() => handlePick(e)}
                  className="px-3 py-2.5 hover:bg-gray-50 cursor-pointer flex items-center justify-between">
                  <span className="text-gray-800 text-sm">{e.nombre || e.razon_social}</span>
                  {e.ruc && <span className="text-gray-400 text-xs ml-2 shrink-0">RUC: {e.ruc}</span>}
                </li>
              ))}
            </ul>
          )}
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-gray-400 text-xs">Sin resultados para "{query.trim()}"</div>
          )}
          {query.trim() && !exactMatch && (
            <div className={filtered.length > 0 ? "border-t border-gray-100" : ""}>
              <button type="button" onMouseDown={() => setClienteModalNombre(query.trim())}
                className="w-full px-3 py-2.5 text-left text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Crear "<strong>{query.trim()}</strong>" como nuevo cliente
              </button>
            </div>
          )}
        </div>
      )}
      {clienteModalNombre !== "" && (
        <EntidadModal
          entidad={{
            _presetRol: "es_cliente",
            _initialNombre: clienteModalNombre,
            _initialRazonSocial: clienteModalNombre,
          }}
          logo={logo}
          token={token}
          allowedRoles={{ es_cliente: true, es_proveedor: false, es_empleado: false }}
          onClose={() => setClienteModalNombre("")}
          onSaved={nuevo => {
            if (nuevo?.id) {
              onCreated?.(nuevo);
              handlePick(nuevo);
            } else {
              setClienteModalNombre("");
            }
          }}
        />
      )}
    </div>
  );
};

const FacturaFormModal = ({
  factura,           // null = nueva, objeto = editar
  sinFactura = false, // true = modo boleta (venta sin comprobante fiscal)
  onClose,
  onSaved,
  token,
  API,
  empresas,          // lista de clientes/empresas
  onEmpresaCreada,   // callback opcional para refrescar la lista del padre al crear un cliente nuevo
  presupuestosDisp,  // todos los presupuestos disponibles
  activeEmpresaPropia,
  hasPermission,
  productos = [],
  productosHabilitados = false,
  cuentasDisp = [],  // cuentas bancarias disponibles
  planCuentasDisp = [],
  defaultEmpresaId = "",
  initialValues = null,
  canUseDescuento = false,
}) => {
  const isEdit = !!factura;
  // En modo edición, respetar sin_factura del registro; en modo nuevo, usar la prop
  const esBoleta = isEdit ? !!factura?.sin_factura : sinFactura;

  const canModoLibre = hasPermission?.("facturas.modo_libre");
  const canCambiarAfectaStock = hasPermission?.("facturas.afectar_stock");
  const canModifyPrecioAbajo = hasPermission?.("facturas.modificar_precio_abajo");
  const canModifyPrecioArriba = hasPermission?.("facturas.modificar_precio_arriba");
  const defaultModo = productosHabilitados ? (canModoLibre ? "libre" : "productos") : "libre";
  // Descuento global: permiso propio, independiente del descuento por ítem.
  const puedeDescuentoGlobal = hasPermission?.("facturas.descuentos_globales");

  const defaultConcepto = () => ({ descripcion: "", cantidad: 1, precio_unitario: 0, precio_costo: 0, descuento_pct: 0, subtotal: 0, producto_id: "", producto_sku: "", variante_id: "", variante_texto: "", iva_tipo: "10", codigo_fabricante: "", lote: "", fecha_vencimiento: "", moneda_item: "", tipo_cambio_item: "", precio_original: null });

  // Convierte un precio expresado en `monedaItem` a la moneda del documento (`docMoneda`),
  // usando el mismo criterio que Presupuestos: PYG→USD/BRL multiplica por TC, USD/BRL→PYG divide.
  const convertirPrecioAMonedaDoc = (precioOriginal, monedaItem, docMoneda, tc) => {
    if (!monedaItem || monedaItem === docMoneda) return precioOriginal;
    const tcNum = parseFloat(tc) || 0;
    if (!tcNum) return precioOriginal;
    if (docMoneda === "PYG" && (monedaItem === "USD" || monedaItem === "BRL")) return precioOriginal * tcNum;
    if ((docMoneda === "USD" || docMoneda === "BRL") && monedaItem === "PYG") return precioOriginal / tcNum;
    return precioOriginal;
  };

  const logoTipo = activeEmpresaPropia?.slug || "arandujar";
  const depVenta = useDepositos(token, logoTipo);

  // Lista local de clientes, fusionada con lo que venga por prop — permite que un
  // cliente recién creado desde el buscador quede disponible al instante, sin
  // esperar a que el padre (VentasPage) refresque su propio fetch.
  const [empresasLocal, setEmpresasLocal] = useState(empresas || []);
  useEffect(() => {
    setEmpresasLocal(prev => {
      const idsProp = new Set((empresas || []).map(e => String(e.id)));
      const soloLocal = prev.filter(e => e._soloLocal && !idsProp.has(String(e.id)));
      return [...(empresas || []), ...soloLocal];
    });
  }, [empresas]);

  const getDefaultForm = () => ({
    numero: "",
    fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
    forma_pago: "contado",
    razon_social: "",
    ruc: "",
    moneda: "PYG",
    tipo_cambio: "",
    estado: "pendiente",
    fecha_vencimiento: "",
    fecha_pago: "",
    cuenta_id: "",
    cuenta_nombre: "",
    plan_cuenta_id: "",
    plan_cuenta_nombre: "",
    es_credito_cuotas: false,
    num_cuotas: 3,
    fecha_primer_vencimiento: "",
    tasa_interes_cuotas: 0,
    presupuesto_ids: [],
    notas: "",
    vendedor_id: "",
    vendedor_nombre: "",
    delivery_repartidor: "",
    delivery_repartidor_id: "",
    delivery_direccion: "",
    delivery_direccion_editada: false,
    delivery_referencia: "",
    modo: defaultModo,
    afecta_stock: !!productosHabilitados,
    entrega_posterior: false,
    deposito_id: "",
    conceptos: [defaultConcepto()],
    descuento_global_tipo: "monto", // "monto" | "pct"
    descuento_global_monto: 0,
    descuento_global_pct: 0,
    // timbrado
    punto_expedicion: "001",
    // internal — no se envía al backend
    _empresa_id: defaultEmpresaId || "",
    _razon_social_locked: false, // true cuando viene de empresa seleccionada
  });

  const [form, setForm] = useState(getDefaultForm());
  const [saving, setSaving] = useState(false);
  // Cobro de venta al contado "a elección": al guardar se pregunta a qué cuenta ingresa.
  const [cfgCobro, setCfgCobro] = useState(null);
  const [cobroPicker, setCobroPicker] = useState(false);
  // Recetario agronómico (solo con el módulo habilitado): receta obligatoria al vender productos controlados
  const [recetaPicker, setRecetaPicker] = useState(null);
  const recetaRef = useRef(null);
  const [cuentaCobroSel, setCuentaCobroSel] = useState("");
  const cuentaCobroRef = useRef("");
  const medioCobroRef = useRef(SIN_MEDIO);
  const cobroConfirmadoRef = useRef(false);
  const [medioCobroSel, setMedioCobroSel] = useState(SIN_MEDIO);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [devolucionActiva, setDevolucionActiva] = useState(false);
  // Cuando un código de barras escaneado matchea un producto con más de una
  // variante activa, guardamos acá los datos para mostrar el selector rápido.
  const [varianteScanPending, setVarianteScanPending] = useState(null); // { prod, variantesActivas, modo: "agregar"|"restar" }
  const [comandaAbierta, setComandaAbierta] = useState(null); // { id, mesa_nombre, numero } — tarjeta Restaurante cargada a la factura
  const [tcInfo, setTcInfo] = useState(null);
  const [presSearch, setPresSearch] = useState("");
  // Presupuestos cargados directamente (sin filtro de período) para que el form
  // siempre tenga acceso a todos, independiente de lo que muestre el tab de ventas
  const [presupuestosAll, setPresupuestosAll] = useState([]);

  // ── Timbrado config ────────────────────────────────────────────
  const [timbradoConfig, setTimbradoConfig] = useState(null);   // null = cargando
  const [loadingTimbrado, setLoadingTimbrado] = useState(false);
  const [loadingNumero, setLoadingNumero] = useState(false);

  // Cada punto de expedición tiene su propio timbrado/vigencia/tipo_emision/
  // modo_numeracion — todo se resuelve en función del punto seleccionado.
  const puntosExpedicion = timbradoConfig?.puntos_expedicion || [];
  const selectedPuntoData = useMemo(
    () => puntosExpedicion.find(p => p.codigo === form.punto_expedicion) || null,
    [puntosExpedicion, form.punto_expedicion]
  );
  const puntoBloqueado = !!timbradoConfig?.punto_bloqueado;
  const timbradoVigente = !!selectedPuntoData?.vigente;
  const esAutoNumerico = selectedPuntoData?.modo_numeracion === "automatico";

  const fetchTimbrado = useCallback(async () => {
    if (!logoTipo || !token) return;
    setLoadingTimbrado(true);
    try {
      const res = await fetch(`${API}/admin/timbrado-vigente/${logoTipo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTimbradoConfig(data.tiene_config ? data : null);
        // Nueva factura: preseleccionar el punto que le corresponde a este usuario
        // (su asignación personal si la tiene, o el punto por defecto de la empresa).
        if (data.tiene_config && !isEdit) {
          // Si viene de un hueco de numeración, se respeta el punto del hueco (no el sugerido del usuario)
          const sugerido = initialValues?.punto_expedicion || data.punto_sugerido || (data.puntos_expedicion || [])[0]?.codigo || "001";
          setForm(prev => ({ ...prev, punto_expedicion: sugerido }));
        }
      }
    } catch (e) { /* no bloquear */ }
    finally { setLoadingTimbrado(false); }
  }, [API, token, logoTipo, isEdit, initialValues?.punto_expedicion]);

  useEffect(() => { fetchTimbrado(); }, [fetchTimbrado]);

  // Entrega posterior: si la empresa lo dejó preseleccionado por defecto, la factura nueva arranca activada.
  useEffect(() => {
    if (isEdit || !logoTipo || !token || !productosHabilitados) return;
    let alive = true;
    fetch(`${API}/admin/entregas/config?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(cfg => {
        if (alive && cfg?.entrega_posterior_default_ventas) setForm(prev => ({ ...prev, entrega_posterior: true }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [API, token, logoTipo, isEdit, productosHabilitados]);

  // ── Impresión automática al guardar (config por empresa/usuario) ────────
  // autoPrintModo: "no" | "a4" | "a4_doble" | "ticket" — ya viene resuelto por el
  // backend (si la empresa tiene "personalizado por usuario" activado, es la
  // preferencia de ESTE usuario, o "no" si todavía no configuró la suya).
  const [autoPrintModo, setAutoPrintModo] = useState("no");
  const imprimirAlGuardar = autoPrintModo !== "no";
  // Tope de ítems por comprobante (configurable en Ventas > Configuración >
  // Impresión) — para que la factura siempre entre bien en A4/A4 doble vía/Compacta.
  const [maxItemsFactura, setMaxItemsFactura] = useState(12);
  useEffect(() => {
    if (!logoTipo || !token) return;
    let cancelled = false;
    fetch(`${API}/admin/configuracion-impresion-facturas?logo_tipo=${logoTipo}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        setAutoPrintModo(data.modo || (data.imprimir_al_guardar ? "a4" : "no"));
        if (Number.isFinite(data.max_items) && data.max_items > 0) setMaxItemsFactura(data.max_items);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [API, token, logoTipo]);

  // Carga TODOS los presupuestos al abrir el form (sin filtro de período)
  // para que el selector siempre muestre la lista completa.
  useEffect(() => {
    if (!token || !API) return;
    let cancelled = false;
    const load = async () => {
      try {
        const qs = logoTipo ? `?logo_tipo=${logoTipo}` : "";
        const r = await fetch(`${API}/admin/presupuestos${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) {
          const data = await r.json();
          setPresupuestosAll(data.filter(p => !p.eliminada));
        }
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [token, API, logoTipo]);

  // El vendedor de la venta YA NO se elige a mano: es automáticamente el
  // usuario que factura (el backend lo fija en create_factura). Esta lista de
  // usuarios de la empresa solo se usa para el selector de REASIGNACIÓN, que
  // aparece nada más al editar una factura existente y solo si el usuario
  // tiene el permiso granular facturas.reasignar_vendedor (ej: jefes que
  // corrigen un error de carga posterior).
  const canReasignarVendedor = !!hasPermission?.("facturas.reasignar_vendedor");
  const [vendedoresList, setVendedoresList] = useState([]);
  useEffect(() => {
    if (!token || !API || !logoTipo) { setVendedoresList([]); return; }
    let cancelled = false;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const pEnt = fetch(`${API}/admin/vendedores/entidades?logo_tipo=${logoTipo}`, h)
      .then(r => r.ok ? r.json() : []).catch(() => []);
    const pUsr = (isEdit && canReasignarVendedor)
      ? fetch(`${API}/admin/vendedores/usuarios?logo_tipo=${logoTipo}`, h).then(r => r.ok ? r.json() : []).catch(() => [])
      : Promise.resolve([]);
    Promise.all([pEnt, pUsr]).then(([ents, usrs]) => {
      if (cancelled) return;
      const e = (Array.isArray(ents) ? ents : []).map(x => ({ id: x.id, nombre: x.nombre, porcentaje_comision: x.porcentaje_comision }));
      const u = (Array.isArray(usrs) ? usrs : []).map(x => ({ id: x.id, nombre: x.nombre, porcentaje_comision: x.porcentaje_comision }));
      setVendedoresList([...e, ...u]);
    });
    return () => { cancelled = true; };
  }, [token, API, logoTipo, isEdit, canReasignarVendedor]);

  // Delivery: productos configurados en Ventas > Configuración > Delivery. Si algún
  // ítem de la factura es uno de ellos, se piden los datos de entrega.
  const canDelivery = !!hasPermission?.("facturas.imprimir_delivery");
  const [deliveryCfg, setDeliveryCfg] = useState({ productos: [] });
  useEffect(() => {
    if (!token || !API || !logoTipo || !canDelivery) { setDeliveryCfg({ productos: [] }); return; }
    let cancelled = false;
    fetch(`${API}/admin/config-delivery?logo_tipo=${logoTipo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { productos: [] })
      .then(c => { if (!cancelled) setDeliveryCfg(c || { productos: [] }); })
      .catch(() => { if (!cancelled) setDeliveryCfg({ productos: [] }); });
    return () => { cancelled = true; };
  }, [token, API, logoTipo, canDelivery]);
  const [repartidores, setRepartidores] = useState([]);
  useEffect(() => {
    if (!token || !API || !logoTipo || !canDelivery) { setRepartidores([]); return; }
    let cancelled = false;
    fetch(`${API}/admin/delivery/repartidores?logo_tipo=${logoTipo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (!cancelled) setRepartidores(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setRepartidores([]); });
    return () => { cancelled = true; };
  }, [token, API, logoTipo, canDelivery]);
  const deliveryIds = (deliveryCfg.productos || []).map(p => p.producto_id);
  const tieneDelivery = canDelivery && deliveryIds.length > 0 && (form.conceptos || []).some(c => c.producto_id && deliveryIds.includes(c.producto_id));
  // Dirección de entrega por defecto = la del cliente en el Catastro (dirección + ciudad).
  const clienteDireccionDefault = (() => {
    const c = (empresas || []).find(e => e.id === form._empresa_id);
    return c ? [c.direccion, c.ciudad].filter(Boolean).join(", ") : "";
  })();

  const fetchSiguienteNumero = async (punto) => {
    if (!logoTipo || !punto) return;
    setLoadingNumero(true);
    try {
      const res = await fetch(`${API}/admin/siguiente-numero-factura/${logoTipo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ punto_expedicion: punto }),
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({
          ...prev,
          numero: data.numero,
          punto_expedicion: punto,
        }));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo obtener el siguiente número");
      }
    } catch (e) { toast.error("Error al obtener número automático"); }
    finally { setLoadingNumero(false); }
  };

  // Populate form when editing
  useEffect(() => {
    if (isEdit && factura) {
      let empresaId = "";
      if (factura.presupuesto_ids?.length > 0) {
        const pres = presupuestosDisp.find(p => factura.presupuesto_ids.includes(p.id));
        if (pres) empresaId = String(pres.empresa_id || "");
      }
      // Si la factura no tiene conceptos[] (legacy), reconstruimos uno desde concepto/monto
      const conceptosFromFac = (factura.conceptos && factura.conceptos.length > 0)
        ? factura.conceptos
        : [{
            descripcion: factura.concepto || "",
            cantidad: 1,
            precio_unitario: factura.monto || 0,
            subtotal: factura.monto || 0,
          }];
      // Si la factura ya tiene pagos[], heredamos cuenta del primero
      const primerPago = (factura.pagos || [])[0] || {};
      // Resolver empresa: prefiero la guardada en factura.empresa_id, sino la inferida del presupuesto
      const empIdFinal = factura.empresa_id || empresaId || "";
      const empData = empIdFinal ? empresas.find(e => String(e.id) === String(empIdFinal)) : null;
      setForm({
        logo_tipo:          factura.logo_tipo || "arandujar",
        tipo:               factura.tipo || "emitida",
        forma_pago:         factura.forma_pago || "contado",
        numero:             factura.numero || "",
        fecha:              factura.fecha ? factura.fecha.slice(0, 10) : "",
        fecha_vencimiento:  factura.fecha_vencimiento ? factura.fecha_vencimiento.slice(0, 10) : "",
        fecha_pago:         factura.fecha_pago ? factura.fecha_pago.slice(0, 10) : "",
        cuenta_id:          factura.cuenta_id || primerPago.cuenta_id || "",
        cuenta_nombre:      factura.cuenta_nombre || primerPago.cuenta_nombre || "",
        plan_cuenta_id:     factura.plan_cuenta_id || "",
        plan_cuenta_nombre: factura.plan_cuenta_nombre || "",
        razon_social:       factura.razon_social || "",
        ruc:                factura.ruc || "",
        empresa_id:         empIdFinal,
        empresa_nombre:     empData?.nombre || factura.empresa_nombre || "",
        concepto:           factura.concepto || "",
        conceptos:          conceptosFromFac.map(c => ({ ...c, producto_id: c.producto_id || "", producto_sku: c.producto_sku || "", variante_id: c.variante_id || "", variante_texto: c.variante_texto || "", iva_tipo: c.iva_tipo || "10", es_interes: !!c.es_interes })),
        modo:               productosHabilitados ? (factura.modo || (conceptosFromFac.some(c => c.producto_id) ? "productos" : "libre")) : "libre",
        afecta_stock:       productosHabilitados ? (canCambiarAfectaStock ? factura.afecta_stock !== false : true) : false,
        entrega_posterior:  !!factura.entrega_posterior,
        deposito_id:        factura.deposito_id || "",
        monto:              factura.monto ?? "",
        moneda:             factura.moneda || "PYG",
        tipo_cambio:        factura.tipo_cambio ?? "",
        estado:             factura.estado || "pendiente",
        notas:              factura.notas || "",
        presupuesto_ids:    factura.presupuesto_ids || (factura.presupuesto_id ? [factura.presupuesto_id] : []),
        vendedor_id:        factura.vendedor_id || "",
        vendedor_nombre:    factura.vendedor_nombre || "",
        delivery_repartidor: factura.delivery?.repartidor || "",
        delivery_repartidor_id: factura.delivery?.repartidor_id || "",
        delivery_direccion:  factura.delivery?.direccion || "",
        delivery_direccion_editada: !!factura.delivery?.direccion,
        delivery_referencia: factura.delivery?.referencia || "",
        punto_expedicion:   factura.punto_expedicion || "001",
        descuento_global_tipo: (factura.descuento_global_pct || 0) > 0 ? "pct" : "monto",
        descuento_global_monto: factura.descuento_global_monto || 0,
        descuento_global_pct: factura.descuento_global_pct || 0,
        _empresa_id:        empIdFinal,
        _razon_social_locked: !!empIdFinal,
      });
    }
  }, [isEdit, factura, presupuestosDisp]);

  const initialSourceId = initialValues?.__source_id || "";

  useEffect(() => {
    if (isEdit || !initialValues) return;
    const defaults = getDefaultForm();
    const conceptos = Array.isArray(initialValues.conceptos) && initialValues.conceptos.length
      ? initialValues.conceptos.map(c => {
          const cantidad = Number(c.cantidad || 1) || 1;
          const precio = Number(c.precio_unitario || 0) || 0;
          const descuento_pct = Math.min(100, Math.max(0, Number(c.descuento_pct || 0) || 0));
          return {
            ...defaultConcepto(),
            ...c,
            cantidad,
            precio_unitario: precio,
            descuento_pct,
            subtotal: c.subtotal ?? (cantidad * precio * (1 - descuento_pct / 100)),
            producto_id: c.producto_id || "",
            producto_sku: c.producto_sku || "",
            variante_id: c.variante_id || "",
            variante_texto: c.variante_texto || "",
            iva_tipo: c.iva_tipo || "10",
          };
        })
      : defaults.conceptos;
    setForm({
      ...defaults,
      ...initialValues,
      conceptos,
      numero: initialValues.numero || defaults.numero,
      fecha: initialValues.fecha || defaults.fecha,
      _empresa_id: initialValues._empresa_id || initialValues.empresa_id || defaults._empresa_id,
      _razon_social_locked: !!(initialValues._razon_social_locked || initialValues._empresa_id || initialValues.empresa_id),
    });
  }, [isEdit, initialSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers / handlers ─────────────────────────────────────────
  // Setter genérico para campos planos del form
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Cotizaciones del día (USD y BRL), cacheadas por fecha — independientes de
  // cuál sea la moneda de cabecera de la factura. Se usan tanto para autocompletar
  // el TC de cabecera (factura en USD/BRL) como para convertir el precio de un
  // ítem del catálogo cargado en una moneda distinta a la de la factura, incluso
  // cuando la factura está en ₲ (antes esto último no tenía ninguna fuente real
  // de TC y caía en un "1" fijo — ver convertirPrecioAMonedaDoc más abajo).
  const [cotizacionesDia, setCotizacionesDia] = useState({}); // { USD: data|null, BRL: data|null }

  useEffect(() => {
    if (!form.fecha) return;
    let cancelled = false;
    setCotizacionesDia({});
    Promise.all([
      fetch(`${API}/admin/cotizaciones/usd?fecha=${form.fecha}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : null).catch(() => null),
      fetch(`${API}/admin/cotizaciones/brl?fecha=${form.fecha}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : null).catch(() => null),
    ]).then(([usd, brl]) => {
      if (cancelled) return;
      setCotizacionesDia({ USD: usd, BRL: brl });
    });
    return () => { cancelled = true; };
  }, [API, token, form.fecha]);

  // Tipo de cambio real del día para una moneda extranjera (USD o BRL), o 0 si
  // todavía no se cargó/no hay cotización disponible para esa fecha.
  const tcParaMoneda = (moneda) => {
    const data = cotizacionesDia[moneda];
    if (!data) return 0;
    return parseFloat(data.tipo_cambio_sugerido || data.venta || 0) || 0;
  };

  // Resuelve el TC a usar al convertir el precio de un ítem cargado en una moneda
  // distinta a la de la factura: prioriza la cotización real del día (cacheada
  // arriba); si no está disponible, usa el TC manual que el usuario haya cargado
  // en la cabecera; si tampoco hay nada, avisa en vez de asumir "1" en silencio.
  const resolverTcConversion = (monedaItem, docMoneda, tipoCambioManual) => {
    if (!monedaItem) return "";
    const monedaExtranjera = monedaItem !== "PYG" ? monedaItem : docMoneda;
    const tc = tcParaMoneda(monedaExtranjera) || parseFloat(tipoCambioManual) || 0;
    if (!tc) {
      toast.error(`No se encontró cotización de ${monedaExtranjera} para ${form.fecha} — el precio del ítem quedó sin convertir, cargá el tipo de cambio manualmente en el ítem.`);
    }
    return tc;
  };

  useEffect(() => {
    const sugeridoActual = tcInfo ? String(tcInfo.tipo_cambio_sugerido || tcInfo.venta || "") : "";
    const tcActual = String(form.tipo_cambio || "");
    const puedeActualizarTc = !tcActual || (sugeridoActual && tcActual === sugeridoActual);
    if ((form.moneda !== "USD" && form.moneda !== "BRL") || !form.fecha || !puedeActualizarTc) return;
    const data = cotizacionesDia[form.moneda];
    if (!data) return;
    const sugerido = data.tipo_cambio_sugerido || data.venta;
    if (sugerido) {
      setForm(prev => {
        const prevTc = String(prev.tipo_cambio || "");
        if (prevTc && sugeridoActual && prevTc !== sugeridoActual) return prev;
        return { ...prev, tipo_cambio: String(sugerido) };
      });
      setTcInfo(data);
    }
  }, [form.moneda, form.fecha, form.tipo_cambio, cotizacionesDia, tcInfo?.tipo_cambio_sugerido, tcInfo?.venta]);


  const addDays = (fecha, dias) => {
    const base = fecha ? new Date(`${fecha}T00:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return fecha || new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
    base.setDate(base.getDate() + Math.max(Number(dias || 0), 0));
    return base.toISOString().slice(0, 10);
  };

  const totalBrutoConceptos = useMemo(
    () => (form.conceptos || []).reduce((s, c) => s + (Number(c.subtotal) || 0), 0),
    [form.conceptos]
  );

  // Descuento global: monto efectivo aplicado sobre el total bruto de conceptos
  // (mismo criterio que en Presupuestos — clamp a 0..total, prorateado por ítem en el backend).
  const descuentoGlobalMonto = useMemo(() => {
    const pct = Math.max(0, Math.min(100, parseFloat(form.descuento_global_pct) || 0));
    const usaPct = form.descuento_global_tipo === "pct";
    const montoIn = Math.max(0, parseFloat(form.descuento_global_monto) || 0);
    return usaPct ? totalBrutoConceptos * (pct / 100) : Math.min(montoIn, totalBrutoConceptos);
  }, [totalBrutoConceptos, form.descuento_global_tipo, form.descuento_global_monto, form.descuento_global_pct]);

  const montoTotalActual = useMemo(
    () => Math.max(0, totalBrutoConceptos - descuentoGlobalMonto),
    [totalBrutoConceptos, descuentoGlobalMonto]
  );

  const usoPlanCuenta = form.forma_pago === "credito" ? "venta_credito" : "venta_contado";
  const planCuentasFiltradas = useMemo(() => {
    return (planCuentasDisp || []).filter(c => c.activa !== false && c.uso === usoPlanCuenta && (!c.logo_tipo || c.logo_tipo === logoTipo));
  }, [planCuentasDisp, usoPlanCuenta, logoTipo]);

  const aplicarPlanCuenta = (cuentaId, recalcularFecha = true) => {
    const cuenta = planCuentasFiltradas.find(c => String(c.id) === String(cuentaId));
    setForm(prev => ({
      ...prev,
      plan_cuenta_id:       cuentaId || "",
      plan_cuenta_nombre:   cuenta?.nombre || "",
      fecha_vencimiento:    cuenta && !cuenta.es_cuotas && recalcularFecha ? (cuenta.fecha_vencimiento_fija || addDays(prev.fecha, cuenta.dias_vencimiento)) : prev.fecha_vencimiento,
      es_credito_cuotas:    !!cuenta?.es_cuotas,
      tasa_interes_cuotas:  cuenta?.tasa_interes ?? 0,
      num_cuotas:           cuenta?.es_cuotas ? Math.min(prev.num_cuotas || 3, cuenta.mi_max_cuotas || cuenta.max_cuotas || 6) : prev.num_cuotas,
    }));
  };

  // Derived: selected plan cuenta object
  const planCuentaSeleccionada = useMemo(
    () => planCuentasFiltradas.find(c => String(c.id) === String(form.plan_cuenta_id)) || null,
    [planCuentasFiltradas, form.plan_cuenta_id]
  );

  // Cuota preview
  const cuotasPreview = useMemo(() => {
    if (!form.es_credito_cuotas || !form.num_cuotas || !montoTotalActual) return [];
    const n = Number(form.num_cuotas);
    const tasa = Number(form.tasa_interes_cuotas || 0);
    const totalConInteres = montoTotalActual * (1 + tasa / 100);
    const base = Math.floor(totalConInteres / n);
    const resto = Math.round(totalConInteres - base * n);
    return Array.from({ length: n }, (_, i) => ({
      num: i + 1,
      monto: i === n - 1 ? base + resto : base,
    }));
  }, [form.es_credito_cuotas, form.num_cuotas, form.tasa_interes_cuotas, montoTotalActual]);

  useEffect(() => {
    if (!planCuentasFiltradas.length) return;
    const actualOk = planCuentasFiltradas.some(c => String(c.id) === String(form.plan_cuenta_id));
    if (actualOk) return;
    const pred = planCuentasFiltradas.find(c => c.predeterminada && !c.es_cuotas) || planCuentasFiltradas.find(c => !c.es_cuotas) || planCuentasFiltradas[0];
    setForm(prev => ({
      ...prev,
      plan_cuenta_id:      pred.id,
      plan_cuenta_nombre:  pred.nombre,
      fecha_vencimiento:   pred.es_cuotas ? prev.fecha_vencimiento : (pred.fecha_vencimiento_fija || addDays(prev.fecha, pred.dias_vencimiento)),
      es_credito_cuotas:   !!pred.es_cuotas,
      tasa_interes_cuotas: pred.tasa_interes ?? 0,
    }));
  }, [planCuentasFiltradas, form.plan_cuenta_id]); // eslint-disable-line

  // Format helper (₲ o USD o BRL según moneda actual)
  const fmtMonto = (n) => {
    const num = Number(n) || 0;
    if (form.moneda === "USD") {
      return `USD ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (form.moneda === "BRL") {
      return `R$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
  };

  // Conceptos / ítems
  const addConcepto = () => {
    if ((form.conceptos || []).length >= maxItemsFactura) {
      toast.error(`Llegaste al máximo de ${maxItemsFactura} ítems por comprobante. Hacé otra factura para el resto, o ajustá el límite en Configuración → Impresión.`);
      return;
    }
    setForm(prev => ({ ...prev, conceptos: [...(prev.conceptos || []), defaultConcepto()] }));
  };
  const removeConcepto = (idx) => {
    setForm(prev => ({ ...prev, conceptos: prev.conceptos.filter((_, i) => i !== idx) }));
  };
  const handleConceptoChange = (idx, key, value) => {
    setForm(prev => {
      const conceptos = (prev.conceptos || []).map((c, i) => {
        if (i !== idx) return c;
        const nuevo = { ...c, [key]: value };
        // Recalcular subtotal si cambió cantidad, precio_unitario o descuento_pct
        if (key === "cantidad" || key === "precio_unitario" || key === "descuento_pct") {
          const cant = parseFloat(key === "cantidad" ? value : c.cantidad) || 0;
          const precio = parseFloat(key === "precio_unitario" ? value : c.precio_unitario) || 0;
          let disc = parseFloat(key === "descuento_pct" ? value : (c.descuento_pct || 0)) || 0;
          // Piso de costo: si tiene precio_costo y opera en modo catálogo, no puede quedar precio efectivo < costo
          if (key === "descuento_pct" && prev.modo === "productos" && c.producto_id) {
            const precioCosto = parseFloat(c.precio_costo) || 0;
            if (precioCosto > 0 && precio > 0) {
              const precioEfectivo = precio * (1 - disc / 100);
              if (precioEfectivo < precioCosto) {
                // Descuento máximo que deja el precio justo en el costo
                disc = Math.max(0, parseFloat(((1 - precioCosto / precio) * 100).toFixed(2)));
                nuevo.descuento_pct = disc;
              }
            }
          }
          nuevo.subtotal = cant * precio * (1 - disc / 100);
        }
        return nuevo;
      });
      return { ...prev, conceptos };
    });
  };

  // Recalcula precio_unitario/subtotal cuando cambia el tipo de cambio de un ítem cuyo
  // producto viene en una moneda distinta a la de la factura (mismo patrón que Presupuestos).
  const handleTipoCambioItemChange = (idx, value) => {
    setForm(prev => {
      const conceptos = (prev.conceptos || []).map((c, i) => {
        if (i !== idx) return c;
        const base = c.precio_original != null ? c.precio_original : c.precio_unitario;
        const precioConvertido = convertirPrecioAMonedaDoc(base, c.moneda_item, prev.moneda, parseFloat(value) || 0);
        const cant = parseFloat(c.cantidad) || 0;
        const disc = parseFloat(c.descuento_pct || 0) || 0;
        return {
          ...c,
          tipo_cambio_item: value,
          precio_unitario: precioConvertido,
          precio_catalogo: precioConvertido,
          subtotal: cant * precioConvertido * (1 - disc / 100),
        };
      });
      return { ...prev, conceptos };
    });
  };

  // Precio bloqueado si el concepto viene del catálogo y el usuario no tiene permisos de modificar
  const isPrecioLocked = (c) => {
    if (form.modo !== "productos") return false;
    if (!c.producto_id) return false;
    return !canModifyPrecioAbajo && !canModifyPrecioArriba;
  };

  const handlePrecioChange = (idx, value) => {
    const conceptos = form.conceptos || [];
    const c = conceptos[idx];
    const isFromCatalog = !!c?.producto_id && form.modo === "productos";
    const nuevo = parseFloat(value);

    // Campo vacío o incompleto: dejar pasar sin validar (usuario está borrando para reescribir)
    if (isNaN(nuevo)) {
      handleConceptoChange(idx, "precio_unitario", value);
      return;
    }

    if (isFromCatalog) {
      const catalogo = parseFloat(c.precio_catalogo ?? c.precio_unitario) || 0;
      if (!canModifyPrecioAbajo && nuevo < catalogo) {
        handleConceptoChange(idx, "precio_unitario", catalogo);
        return;
      }
      if (!canModifyPrecioArriba && nuevo > catalogo) {
        handleConceptoChange(idx, "precio_unitario", catalogo);
        return;
      }
      // Piso de costo: se valida en onBlur para no bloquear valores intermedios al escribir
    }
    handleConceptoChange(idx, "precio_unitario", value);
  };

  // Valida piso de costo al salir del campo (no en cada tecla)
  const handlePrecioBlur = (idx, value) => {
    const conceptos = form.conceptos || [];
    const c = conceptos[idx];
    const isFromCatalog = !!c?.producto_id && form.modo === "productos";
    if (!isFromCatalog) return;
    const nuevo = parseFloat(value) || 0;
    const catalogo = parseFloat(c.precio_catalogo ?? c.precio_unitario) || 0;
    // Campo vacío o cero al salir → restaurar al mínimo permitido
    if (!nuevo) {
      // Nunca se asume el costo como precio de venta: vuelve al precio de catálogo (o queda en 0 y no deja facturar)
      handleConceptoChange(idx, "precio_unitario", catalogo);
      if (!catalogo) toast.error("Este producto no tiene precio de venta. Cargá un precio para poder facturarlo.");
      return;
    }
    // Piso de costo: con permiso de bajar, no puede quedar por debajo del costo de compra
    if (canModifyPrecioAbajo) {
      const precioCosto = parseFloat(c.precio_costo) || 0;
      if (precioCosto > 0 && nuevo < precioCosto) {
        handleConceptoChange(idx, "precio_unitario", precioCosto);
      }
    }
  };

  const appendProductoConcepto = (prod, incrementIfExists = true, variante = null) => {
    if (!prod) return false;
    if (prod.variantes_habilitadas && !variante) {
      toast.error("Elegí la variante del producto");
      return false;
    }
    // Tope de ítems: solo bloquea si esto agregaría una línea NUEVA (no cuenta
    // cuando el producto ya está y solo se le suma cantidad, ni cuando hay una
    // fila vacía para reutilizar).
    const conceptosActuales = form.conceptos || [];
    const yaExiste = incrementIfExists && conceptosActuales.some(c =>
      String(c.producto_id) === String(prod.id) && String(c.variante_id || "") === String(variante?.id || "")
    );
    const hayVacio = conceptosActuales.some(c => !c.descripcion && !c.producto_id && !(parseFloat(c.precio_unitario) > 0));
    if (!yaExiste && !hayVacio && conceptosActuales.length >= maxItemsFactura) {
      toast.error(`Llegaste al máximo de ${maxItemsFactura} ítems por comprobante. Hacé otra factura para el resto, o ajustá el límite en Configuración → Impresión.`);
      return false;
    }
    const ivaTipo = prod.iva_tipo || "10";
    const precio = parseFloat(variante?.precio_venta ?? prod.precio_venta) || 0;
    setForm(prev => {
      const conceptos = [...(prev.conceptos || [])];
      if (incrementIfExists) {
        const idx = conceptos.findIndex(c => String(c.producto_id) === String(prod.id) && String(c.variante_id || "") === String(variante?.id || ""));
        if (idx >= 0) {
          const cant = (parseFloat(conceptos[idx].cantidad) || 0) + 1;
          const disc0 = parseFloat(conceptos[idx].descuento_pct || 0) || 0;
          conceptos[idx] = {
            ...conceptos[idx],
            cantidad: cant,
            subtotal: cant * (parseFloat(conceptos[idx].precio_unitario) || 0) * (1 - disc0 / 100),
          };
          return { ...prev, conceptos, modo: productosHabilitados ? "productos" : prev.modo, afecta_stock: productosHabilitados ? true : prev.afecta_stock };
        }
      }
      const vacio = conceptos.findIndex(c => !c.descripcion && !c.producto_id && !(parseFloat(c.precio_unitario) > 0));
      // Si el producto está cargado en el catálogo en una moneda distinta a la de esta factura,
      // convertimos el precio a la moneda del documento (mismo mecanismo que en Presupuestos).
      // Los productos sin moneda cargada (catálogo viejo, o nunca se tocó ese campo) están en
      // Guaraníes por defecto — antes, al no tener prod.moneda, nunca se disparaba la conversión
      // aunque la factura fuera en USD/BRL, y el precio en ₲ quedaba tal cual.
      const monedaProdEfectiva = prod.moneda || "PYG";
      const monedaItem = monedaProdEfectiva !== prev.moneda ? monedaProdEfectiva : "";
      const tcEfectivo = resolverTcConversion(monedaItem, prev.moneda, prev.tipo_cambio);
      const precioConvertido = convertirPrecioAMonedaDoc(precio, monedaItem, prev.moneda, tcEfectivo);
      const nuevo = {
        descripcion: prod.nombre,
        cantidad: 1,
        precio_unitario: precioConvertido,
        precio_catalogo: precioConvertido,
        precio_costo: parseFloat(variante?.precio_costo ?? prod.precio_costo) || 0,
        descuento_pct: 0,
        subtotal: precioConvertido,
        producto_id: prod.id,
        producto_sku: prod.sku || "",
        variante_id: variante?.id || "",
        variante_texto: variante?.atributos_texto || "",
        iva_tipo: ivaTipo,
        codigo_fabricante: prod.codigo_fabricante || "",
        lote: prod.lote || "",
        fecha_vencimiento: prod.fecha_vencimiento || "",
        moneda_item: monedaItem,
        tipo_cambio_item: monedaItem ? String(tcEfectivo) : "",
        precio_original: monedaItem ? precio : null,
      };
      if (vacio >= 0) conceptos[vacio] = nuevo;
      else conceptos.push(nuevo);
      return {
        ...prev,
        conceptos,
        modo: productosHabilitados ? "productos" : prev.modo,
        afecta_stock: productosHabilitados ? true : prev.afecta_stock,
      };
    });
    return true;
  };

  // Modo Devolución: cada escaneo resta 1 a la cantidad del concepto coincidente en la factura.
  // Si llega a 0, se quita la línea. No mueve stock por sí solo, solo edita el ítem en el formulario.
  const restarProductoConcepto = (prod, variante = null) => {
    let resultado = null; // "restado" | "eliminado" | "no_encontrado"
    setForm(prev => {
      const conceptos = [...(prev.conceptos || [])];
      const idx = conceptos.findIndex(c =>
        String(c.producto_id || "") === String(prod.id || "") &&
        String(c.variante_id || "") === String(variante?.id || "")
      );
      if (idx < 0) {
        resultado = "no_encontrado";
        return prev;
      }
      const cantidadActual = parseFloat(conceptos[idx].cantidad) || 0;
      const cantidad = cantidadActual - 1;
      if (cantidad <= 0) {
        resultado = "eliminado";
        if (conceptos.length === 1) {
          conceptos[idx] = { ...conceptos[idx], cantidad: 0, subtotal: 0 };
          return { ...prev, conceptos };
        }
        conceptos.splice(idx, 1);
        return { ...prev, conceptos };
      }
      resultado = "restado";
      const disc = parseFloat(conceptos[idx].descuento_pct || 0) || 0;
      conceptos[idx] = {
        ...conceptos[idx],
        cantidad,
        subtotal: cantidad * (parseFloat(conceptos[idx].precio_unitario) || 0) * (1 - disc / 100),
      };
      return { ...prev, conceptos };
    });
    return resultado;
  };

  // Carga el consumo pendiente de una tarjeta de comanda (módulo Restaurante)
  // como conceptos de la factura. Los ítems ya vienen con nombre/precio/moneda
  // propios (no dependen del catálogo local), se agregan directo sin pasar por
  // appendProductoConcepto (que espera un objeto "producto" del catálogo).
  const cargarComandaEnFactura = (comanda) => {
    const itemsComanda = comanda.items || [];
    if (itemsComanda.length === 0) {
      toast.error("Esta tarjeta no tiene consumo cargado");
      return false;
    }
    const nuevos = itemsComanda.map(it => {
      const monedaItem = (it.moneda_producto && it.moneda_producto !== form.moneda) ? it.moneda_producto : "";
      const tcEfectivo = resolverTcConversion(monedaItem, form.moneda, form.tipo_cambio);
      const precio = Number(it.precio_unitario || 0);
      const precioConvertido = convertirPrecioAMonedaDoc(precio, monedaItem, form.moneda, tcEfectivo);
      return {
        descripcion: it.nombre,
        cantidad: Number(it.cantidad || 1),
        precio_unitario: precioConvertido,
        precio_catalogo: precioConvertido,
        precio_costo: 0,
        descuento_pct: 0,
        subtotal: precioConvertido * Number(it.cantidad || 1),
        producto_id: it.producto_id || it.id || "",
        producto_sku: "",
        variante_id: "",
        variante_texto: "",
        iva_tipo: "10",
        codigo_fabricante: "",
        lote: "",
        fecha_vencimiento: "",
        moneda_item: monedaItem,
        tipo_cambio_item: monedaItem ? String(tcEfectivo) : "",
        precio_original: monedaItem ? precio : null,
      };
    });
    setForm(prev => {
      const conceptosActuales = (prev.conceptos || []).filter(c => c.descripcion || c.producto_id || (parseFloat(c.precio_unitario) > 0));
      return {
        ...prev,
        conceptos: [...conceptosActuales, ...nuevos],
        modo: productosHabilitados ? "productos" : prev.modo,
        afecta_stock: productosHabilitados ? true : prev.afecta_stock,
      };
    });
    setComandaAbierta({ id: comanda.id, mesa_nombre: comanda.mesa_nombre || "", numero: comanda.numero });
    toast.success(`Comanda ${comanda.mesa_nombre || comanda.numero} cargada a la factura`);
    return true;
  };

  const handleBarcodeSubmit = async () => {
    const code = (barcodeInput || "").trim();
    if (!code) return;
    // Primero probamos si el código escaneado es una tarjeta de comanda del
    // módulo Restaurante (solo al agregar, no en modo devolución) — si no
    // existe, cae sin romper nada al flujo normal de búsqueda de producto.
    if (!devolucionActiva && API && token) {
      try {
        const qC = new URLSearchParams();
        if (logoTipo) qC.set("logo_tipo", logoTipo);
        const rComanda = await fetch(`${API}/admin/restaurante/comandas/by-barcode/${encodeURIComponent(code)}?${qC}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (rComanda.ok) {
          const comanda = await rComanda.json();
          if (cargarComandaEnFactura(comanda)) setBarcodeInput("");
          return;
        }
      } catch (_e) {
        // Restaurante no habilitado o error de red: seguimos con producto normal.
      }
    }
    let prod = productos.find(p => (p.codigo_barras || "").trim() === code && p.activo !== false && (!p.logo_tipo || p.logo_tipo === logoTipo));
    if (!prod && API && token) {
      try {
        setBarcodeLoading(true);
        const q = new URLSearchParams();
        if (logoTipo) q.set("logo_tipo", logoTipo);
        const res = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}?${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) prod = await res.json();
        else throw new Error((await res.json().catch(() => ({}))).detail || "Producto no encontrado");
      } catch (e) {
        toast.error(e.message || "No se encontró el producto");
        return;
      } finally {
        setBarcodeLoading(false);
      }
    }
    if (!prod) {
      toast.error("No se encontró producto con ese código de barras");
      return;
    }
    const variantesActivas = (prod.variantes || []).filter(v => v.activo !== false);
    const varianteMatch = prod.variantes_habilitadas && variantesActivas.length === 1 ? variantesActivas[0] : null;

    // Mismo código de barras con más de una variante activa (ej. mismo modelo,
    // distintos talles/colores): no podemos adivinar cuál es, mostramos un
    // selector rápido para que el usuario elija.
    if (prod.variantes_habilitadas && variantesActivas.length > 1) {
      setVarianteScanPending({
        prod,
        variantesActivas,
        modo: devolucionActiva ? "restar" : "agregar",
      });
      setBarcodeInput("");
      return;
    }

    if (devolucionActiva) {
      const resultado = restarProductoConcepto(prod, varianteMatch);
      if (resultado === "no_encontrado") {
        toast.error(`${prod.nombre} no está cargado en esta factura`);
      } else if (resultado === "eliminado") {
        toast.success(`${prod.nombre} quitado de la factura`);
      } else {
        toast.success(`${prod.nombre}: −1 unidad`);
      }
    } else {
      const agregado = appendProductoConcepto(prod, true, varianteMatch);
      if (agregado) toast.success(`${prod.nombre} agregado`);
    }
    setBarcodeInput("");
  };

  // Confirma la variante elegida en el selector rápido de escaneo.
  const handleVarianteScanSelect = (variante) => {
    if (!varianteScanPending) return;
    const { prod, modo } = varianteScanPending;
    const etiqueta = variante.atributos_texto || "variante";
    if (modo === "restar") {
      const resultado = restarProductoConcepto(prod, variante);
      if (resultado === "no_encontrado") {
        toast.error(`${prod.nombre} (${etiqueta}) no está cargado en esta factura`);
      } else if (resultado === "eliminado") {
        toast.success(`${prod.nombre} (${etiqueta}) quitado de la factura`);
      } else {
        toast.success(`${prod.nombre} (${etiqueta}): −1 unidad`);
      }
    } else {
      const agregado = appendProductoConcepto(prod, true, variante);
      if (agregado) toast.success(`${prod.nombre} (${etiqueta}) agregado`);
    }
    setVarianteScanPending(null);
  };

  const selectProductoForConcepto = (idx, productoId, prodFromSelect = null, variante = null) => {
    const prod = productos.find(p => String(p.id) === String(productoId) && (!p.logo_tipo || p.logo_tipo === logoTipo));
    if (!prod) {
      setForm(prev => ({
        ...prev,
        conceptos: (prev.conceptos || []).map((c, i) => i === idx ? { ...c, producto_id: "", producto_sku: "", variante_id: "", variante_texto: "" } : c)
      }));
      return;
    }
    setForm(prev => {
      const conceptos = (prev.conceptos || []).map((c, i) => {
        if (i !== idx) return c;
        const selectedVariant = variante || prodFromSelect?.variantes?.find(v => v.id === c.variante_id) || null;
        const precio = Number(selectedVariant?.precio_venta ?? prod.precio_venta ?? prod.precio ?? c.precio_unitario ?? 0) || 0;
        const cantidad = Number(c.cantidad || 1) || 1;
        // Ídem appendProductoConcepto: sin moneda cargada, el producto está en ₲ por defecto.
        const monedaProdEfectiva = prod.moneda || "PYG";
        const monedaItem = monedaProdEfectiva !== prev.moneda ? monedaProdEfectiva : "";
        const tcEfectivo = resolverTcConversion(monedaItem, prev.moneda, prev.tipo_cambio);
        const precioConvertido = convertirPrecioAMonedaDoc(precio, monedaItem, prev.moneda, tcEfectivo);
        return {
          ...c,
          producto_id: prod.id,
          producto_sku: prod.sku || "",
          variante_id: selectedVariant?.id || "",
          variante_texto: selectedVariant?.atributos_texto || "",
          descripcion: prod.nombre || c.descripcion,
          precio_unitario: precioConvertido,
          precio_catalogo: precioConvertido,
          precio_costo: parseFloat(selectedVariant?.precio_costo ?? prod.precio_costo) || 0,
          descuento_pct: 0,
          subtotal: cantidad * precioConvertido,
          iva_tipo: prod.iva_tipo || c.iva_tipo || "10",
          codigo_fabricante: prod.codigo_fabricante || "",
          lote: prod.lote || "",
          fecha_vencimiento: prod.fecha_vencimiento || "",
          moneda_item: monedaItem,
          tipo_cambio_item: monedaItem ? String(tcEfectivo) : "",
          precio_original: monedaItem ? precio : null,
        };
      });
      return { ...prev, conceptos };
    });
  };

  // Total derivado (ya neto del descuento global)
  const totalMonto = montoTotalActual;

  // Total en letras — mismo algoritmo que usa el modal de impresión
  // (VentasPage.jsx), duplicado acá para poder mostrarlo ya en el formulario,
  // antes de guardar/imprimir (pedido explícito: que el detalle se vea acá
  // también, no solo al imprimir).
  const numToLetras = (n) => {
    const num = Math.round(Math.abs(n || 0));
    if (num === 0) return "Cero";
    const un = ["","un","dos","tres","cuatro","cinco","seis","siete","ocho","nueve","diez","once","doce","trece","catorce","quince","dieciséis","diecisiete","dieciocho","diecinueve"];
    const dec = ["","","veinte","treinta","cuarenta","cincuenta","sesenta","setenta","ochenta","noventa"];
    const cen = ["","cien","doscientos","trescientos","cuatrocientos","quinientos","seiscientos","setecientos","ochocientos","novecientos"];
    const g = (x) => {
      if (x < 20) return un[x];
      const d = Math.floor(x/10), u = x%10;
      if (x < 100) return dec[d] + (u ? " y " + un[u] : "");
      const c2 = Math.floor(x/100), r = x%100;
      return (c2===1&&r>0?"ciento ":cen[c2]+(r?" ":"")) + (r?g(r):"");
    };
    const mil = (x) => x===0?"":x===1?"un mil ":g(x)+" mil ";
    const m = Math.floor(num/1000000), rest = num%1000000;
    const k = Math.floor(rest/1000), u2 = rest%1000;
    let r = (m>0?(m===1?"un millón ":g(m)+" millones "):"") + mil(k) + (u2>0?g(u2):"");
    return r.trim().charAt(0).toUpperCase() + r.trim().slice(1);
  };
  const totalEnLetras = form.moneda === "PYG"
    ? numToLetras(totalMonto) + " guaraníes"
    : numToLetras(totalMonto) + " " + (form.moneda || "");

  // IVA breakdown por tipo — el descuento global se prorratea proporcionalmente
  // entre los tres tramos, igual que hace el backend a nivel de ítem.
  const ivaBreakdown = useMemo(() => {
    let exenta = 0, grav5 = 0, grav10 = 0;
    (form.conceptos || []).forEach(c => {
      const sub = Number(c.subtotal) || 0;
      const tipo = c.iva_tipo || "10";
      if (tipo === "exenta") exenta += sub;
      else if (tipo === "5") grav5 += sub;
      else grav10 += sub;
    });
    const totalBruto = exenta + grav5 + grav10;
    if (descuentoGlobalMonto > 0 && totalBruto > 0) {
      const factor = 1 - Math.min(1, descuentoGlobalMonto / totalBruto);
      exenta *= factor; grav5 *= factor; grav10 *= factor;
    }
    const isDecimal = form.moneda !== "PYG";
    const iva5 = isDecimal ? Math.round((grav5 / 21) * 100) / 100 : Math.round(grav5 / 21);
    const iva10 = isDecimal ? Math.round((grav10 / 11) * 100) / 100 : Math.round(grav10 / 11);
    return { exenta, grav5, grav10, iva5, iva10, total: iva5 + iva10 };
  }, [form.conceptos, descuentoGlobalMonto, form.moneda]);

  // Cliente / empresa
  const handleEmpresaChange = (empresaId) => {
    if (!empresaId) {
      setForm(prev => ({
        ...prev,
        _empresa_id: "",
        _razon_social_locked: false,
        razon_social: "",
        ruc: "",
        empresa_id: "",
        empresa_nombre: "",
      }));
      return;
    }
    const emp = empresasLocal.find(e => String(e.id) === String(empresaId));
    setForm(prev => ({
      ...prev,
      _empresa_id: empresaId,
      _razon_social_locked: true,
      razon_social: emp?.razon_social || emp?.nombre || prev.razon_social,
      ruc: emp?.ruc || "",
      empresa_id: empresaId,
      empresa_nombre: emp?.nombre || emp?.razon_social || "",
      presupuesto_ids: [], // limpiar selección al cambiar de empresa
    }));
  };

  // Selección directa desde el buscador (existente o recién creado) — evita depender
  // de que empresasLocal ya tenga el objeto cuando viene de una creación inline.
  const applyEmpresaSeleccionada = (emp) => {
    if (!emp?.id) { handleEmpresaChange(""); return; }
    setForm(prev => ({
      ...prev,
      _empresa_id: emp.id,
      _razon_social_locked: true,
      razon_social: emp.razon_social || emp.nombre || "",
      ruc: emp.ruc || "",
      empresa_id: emp.id,
      empresa_nombre: emp.nombre || emp.razon_social || "",
      presupuesto_ids: [],
    }));
  };

  const handleClienteCreado = (nuevo) => {
    setEmpresasLocal(prev => prev.some(e => String(e.id) === String(nuevo.id)) ? prev : [...prev, { ...nuevo, _soloLocal: true }]);
    onEmpresaCreada?.();
  };

  // Si hay defaultEmpresaId y es una factura nueva, pre-cargar la empresa al montar
  useEffect(() => {
    if (!isEdit && defaultEmpresaId && empresasLocal.length > 0) {
      handleEmpresaChange(defaultEmpresaId);
    }
    // eslint-disable-next-line
  }, [defaultEmpresaId, empresasLocal.length]);

  // Fuente de presupuestos: preferimos presupuestosAll (cargados sin filtro de período).
  // Fallback a presupuestosDisp si aún no cargaron.
  const presupuestosFuente = useMemo(
    () => presupuestosAll.length > 0 ? presupuestosAll : (presupuestosDisp || []),
    [presupuestosAll, presupuestosDisp],
  );

  // Presupuestos vinculables: solo aprobados o facturados (parcialmente facturados)
  // Excluye: borrador, enviado, rechazado, cobrado
  // Sin cliente seleccionado → lista vacía
  // Acepta empresa_id (legacy db.empresas) o entidad_id (db.entidades catastro)
  const presupuestosFiltrados = useMemo(() => {
    if (!form._empresa_id) return [];
    return presupuestosFuente.filter(p =>
      (p.estado === "aprobado" || p.estado === "facturado") &&
      (String(p.empresa_id) === String(form._empresa_id) ||
       String(p.entidad_id || "") === String(form._empresa_id))
    );
  }, [presupuestosFuente, form._empresa_id]);

  const conceptosVacios = (conceptos = []) => {
    const validos = conceptos.filter(c =>
      c.descripcion || Number(c.cantidad) > 1 || Number(c.precio_unitario) > 0 || Number(c.subtotal) > 0 || c.producto_id
    );
    return validos.length === 0;
  };

  const mapPresupuestoItemsToConceptos = (presupuestos = []) => {
    return presupuestos.flatMap(p => {
      // Con aprobación parcial se factura solo lo aprobado que todavía no se facturó
      const parcial = (p.aprobaciones || []).length > 0;
      return (p.items || []).map((item, itemIdx) => {
        if (parcial && !(Number(item.pendiente_facturar) > 0)) return null;
        const cantidadTotal = Number(item.cantidad || 1) || 1;
        const cantidad = parcial ? Number(item.pendiente_facturar) : cantidadTotal;
        const precio = Number(item.precio_unitario ?? item.precio ?? 0) || 0;
        const descuentoPct = Math.min(100, Math.max(0, Number(item.descuento_pct || 0) || 0));
        const subtotalTotal = item.subtotal !== undefined && item.subtotal !== null
          ? Number(item.subtotal)
          : cantidadTotal * precio * (1 - descuentoPct / 100);
        const subtotal = parcial ? subtotalTotal * cantidad / cantidadTotal : subtotalTotal;
        return {
          presupuesto_item_ref: { presupuesto_id: p.id, idx: itemIdx },
          descripcion: item.descripcion || "",
          cantidad,
          precio_unitario: precio,
          descuento_pct: descuentoPct,
          subtotal,
          producto_id: item.producto_id || "",
          producto_sku: item.producto_sku || "",
          variante_id: item.variante_id || "",
          variante_texto: item.variante_texto || "",
          iva_tipo: item.iva_tipo || "10",
        };
      }).filter(item => item && item.descripcion && (item.precio_unitario > 0 || (item.producto_id && deliveryIds.includes(item.producto_id))));
    });
  };

  // Descuento global del presupuesto → monto equivalente, proporcional a lo que se carga (aprobación parcial).
  const descuentoGlobalDePresupuestos = (presupuestos = []) => {
    let total = 0;
    presupuestos.forEach(p => {
      const items = p.items || [];
      const parcial = (p.aprobaciones || []).length > 0;
      const brutoTotal = items.reduce((s, it) => s + (Number(it.subtotal ?? (Number(it.cantidad || 0) * Number(it.precio_unitario ?? it.precio ?? 0))) || 0), 0);
      if (brutoTotal <= 0) return;
      const pct = Number(p.descuento_global_pct || 0);
      const globalMonto = pct > 0 ? brutoTotal * pct / 100 : Number(p.descuento_global_monto || 0);
      if (globalMonto <= 0) return;
      const cargado = items.reduce((s, it) => {
        const cant = Number(it.cantidad || 0) || 1;
        const sub = Number(it.subtotal ?? (cant * Number(it.precio_unitario ?? it.precio ?? 0))) || 0;
        const q = parcial ? Math.max(0, Number(it.pendiente_facturar || 0)) : cant;
        return s + sub * q / cant;
      }, 0);
      total += globalMonto * Math.min(1, cargado / brutoTotal);
    });
    return Math.round(total);
  };

  const applyPresupuestosToForm = (prev, presupuestos) => {
    const presupuestosConItems = presupuestos.filter(p => (p.items || []).length > 0);
    if (presupuestosConItems.length === 0) return prev;

    const conceptos = mapPresupuestoItemsToConceptos(presupuestosConItems);
    if (conceptos.length === 0) return prev;

    const primero = presupuestosConItems[0];
    const tieneProductos = conceptos.some(c => c.producto_id);
    return {
      ...prev,
      forma_pago: primero.forma_pago || prev.forma_pago,
      moneda: primero.moneda || prev.moneda,
      tipo_cambio: primero.tipo_cambio ?? prev.tipo_cambio,
      modo: productosHabilitados && tieneProductos ? "productos" : prev.modo,
      afecta_stock: productosHabilitados && tieneProductos ? true : prev.afecta_stock,
      conceptos,
      ...(descuentoGlobalDePresupuestos(presupuestosConItems) > 0
        ? { descuento_global_tipo: "monto", descuento_global_monto: descuentoGlobalDePresupuestos(presupuestosConItems), descuento_global_pct: 0 }
        : {}),
    };
  };

  // Suma los ítems de UN presupuesto recién vinculado a los conceptos que ya
  // tenía la factura, en vez de reemplazarlos. Antes, applyPresupuestosToForm
  // (arriba) solo se llamaba automáticamente para el PRIMER presupuesto
  // vinculado (togglePresupuesto exigía conceptosVacios) — al vincular un
  // segundo o tercer presupuesto, ese link se guardaba pero sus ítems nunca
  // se sumaban al monto/detalle de la factura salvo que el usuario tocara
  // manualmente "Cargar ítems del presupuesto" de nuevo (bug real, reportado).
  // Con esto, cada presupuesto que se vincula suma sus ítems automáticamente,
  // sin pisar lo que ya había cargado (manual o de otro presupuesto).
  const appendPresupuestoItemsToForm = (prev, presupuesto) => {
    const nuevosConceptos = mapPresupuestoItemsToConceptos([presupuesto]);
    if (nuevosConceptos.length === 0) return prev;
    const baseConceptos = conceptosVacios(prev.conceptos) ? [] : prev.conceptos;
    const conceptos = [...baseConceptos, ...nuevosConceptos];
    const tieneProductos = conceptos.some(c => c.producto_id);
    const esPrimero = baseConceptos.length === 0;
    return {
      ...prev,
      forma_pago: esPrimero ? (presupuesto.forma_pago || prev.forma_pago) : prev.forma_pago,
      moneda: esPrimero ? (presupuesto.moneda || prev.moneda) : prev.moneda,
      tipo_cambio: esPrimero ? (presupuesto.tipo_cambio ?? prev.tipo_cambio) : prev.tipo_cambio,
      modo: productosHabilitados && tieneProductos ? "productos" : prev.modo,
      afecta_stock: productosHabilitados && tieneProductos ? true : prev.afecta_stock,
      conceptos,
      ...(descuentoGlobalDePresupuestos([presupuesto]) > 0
        ? {
            descuento_global_tipo: "monto", descuento_global_pct: 0,
            descuento_global_monto: (esPrimero ? 0 : (prev.descuento_global_tipo === "monto" ? Number(prev.descuento_global_monto) || 0 : 0)) + descuentoGlobalDePresupuestos([presupuesto]),
          }
        : {}),
    };
  };

  const togglePresupuesto = async (id) => {
    const presupuesto = presupuestosFuente.find(p => String(p.id) === String(id));
    const isAdding = !form.presupuesto_ids.includes(id);
    // Actualizar ids inmediatamente
    setForm(prev => ({
      ...prev,
      presupuesto_ids: isAdding
        ? [...prev.presupuesto_ids, id]
        : prev.presupuesto_ids.filter(x => x !== id),
    }));
    // Auto-sumar ítems del presupuesto que se acaba de vincular (sea el primero,
    // el segundo o el que sea) — se agregan a lo que ya había, no lo reemplazan.
    if (isAdding && presupuesto) {
      const completo = await fetchPresupuestoCompleto(presupuesto);
      setForm(prev => appendPresupuestoItemsToForm(prev, completo));
    }
  };

  // Fetch completo de un presupuesto (con items) cuando el listado los tiene stripeados
  const fetchPresupuestoCompleto = async (p) => {
    if (p.items && p.items.length > 0) return p;
    try {
      const r = await fetch(`${API}/admin/presupuestos/${p.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) return await r.json();
    } catch {}
    return p;
  };

  const cargarItemsPresupuestos = async () => {
    const seleccionados = presupuestosFuente.filter(p => form.presupuesto_ids.includes(p.id));
    if (seleccionados.length === 0) {
      toast.error("Primero vinculá al menos un presupuesto");
      return;
    }
    // Fetch individual para cualquier presupuesto sin items en el listado
    const completos = await Promise.all(seleccionados.map(fetchPresupuestoCompleto));
    const conceptos = mapPresupuestoItemsToConceptos(completos);
    if (conceptos.length === 0) {
      toast.error("El presupuesto vinculado no tiene ítems para cargar");
      return;
    }
    if (!conceptosVacios(form.conceptos) && !window.confirm("Esto reemplazará los ítems actuales de la factura. ¿Continuar?")) {
      return;
    }
    setForm(prev => applyPresupuestosToForm(prev, completos));
    toast.success("Ítems del presupuesto cargados en la factura");
  };

  useEffect(() => {
    const lt = form.logo_tipo || logoTipo;
    if (!token || !lt) return;
    fetch(`${API}/admin/config-ventas-cobro?logo_tipo=${encodeURIComponent(lt)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(c => setCfgCobro(c)).catch(() => {});
  }, [token, form.logo_tipo, logoTipo]); // eslint-disable-line

  const cobroAEleccion = !!cfgCobro?.cobro_automatico_contado && cfgCobro?.cuenta_cobro_automatico_id === "__elegir__";
  const procesadorasCobro = useProcesadoras(API, token, form.logo_tipo || logoTipo);
  // El popup de cobro se muestra si hay que elegir cuenta, o si hay procesadoras para elegir el medio.
  const pedirMedioCobro = !!cfgCobro?.cobro_automatico_contado && (cobroAEleccion || procesadorasCobro.length > 0);
  const cuentasParaCobro = (cuentasDisp || []).filter(c => c.activa !== false && (c.moneda || "PYG") === (form.moneda || "PYG"));

  // Submit
  const handleSave = async () => {
    if (!esBoleta && !form.numero && !isEdit) {
      toast.error("El número de factura es obligatorio");
      return;
    }
    if (!form.fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    if (!form._empresa_id) {
      if (!isEdit) {
        toast.error("Seleccioná o creá un cliente antes de guardar");
        return;
      }
      // Edición de factura histórica sin cliente vinculado: se mantiene la validación anterior.
      if (!form.razon_social?.trim()) {
        toast.error("La razón social es obligatoria");
        return;
      }
    }
    // Bloquear si el timbrado del punto seleccionado está vencido (solo para facturas nuevas emitidas, no boletas)
    if (!isEdit && !esBoleta && timbradoConfig && !timbradoVigente) {
      toast.error(`Timbrado vencido en el punto ${form.punto_expedicion} (${selectedPuntoData?.timbrado_activo?.fecha_vigencia || "-"}). Actualizá el timbrado antes de emitir facturas.`);
      return;
    }
    const esItemDelivery = (c) => !!c.producto_id && deliveryIds.includes(c.producto_id); // delivery puede ir con costo 0
    const sinPrecio = (form.conceptos || []).find(c => c.descripcion && (parseFloat(c.cantidad) || 0) > 0 && !c.es_interes && !esItemDelivery(c) && (parseFloat(c.precio_unitario) || 0) <= 0);
    if (sinPrecio) {
      toast.error(`El ítem «${sinPrecio.descripcion}» no tiene precio de venta. No se puede facturar sin precio.`);
      return;
    }
    const conceptosValidos = (form.conceptos || []).filter(c =>
      c.descripcion && (parseFloat(c.cantidad) || 0) > 0 && ((parseFloat(c.precio_unitario) || 0) > 0 || esItemDelivery(c))
    );
    if (conceptosValidos.length === 0) {
      toast.error("Agregá al menos un ítem con descripción, cantidad y precio");
      return;
    }
    const faltaVariante = form.modo === "productos" && conceptosValidos.some(c => {
      if (!c.producto_id) return false;
      const prod = productos.find(p => String(p.id) === String(c.producto_id));
      return prod?.variantes_habilitadas && !c.variante_id;
    });
    if (faltaVariante) {
      toast.error("Hay productos con variantes sin variante seleccionada");
      return;
    }
    if (!form.plan_cuenta_id) {
      toast.error("Seleccioná una cuenta del plan de cuentas");
      return;
    }
    if (form.es_credito_cuotas && !form.fecha_primer_vencimiento) {
      toast.error("Ingresá la fecha del primer vencimiento para las cuotas");
      return;
    }
    const cuentaSeleccionada = cuentasDisp.find(c => String(c.id) === String(form.cuenta_id));
    const monedaCuenta = cuentaSeleccionada?.moneda || form.moneda || "PYG";
    const estadoOut = isEdit ? (factura?.estado || "pendiente") : "pendiente";
    const requiereConversion = estadoOut === "pagada" && form.cuenta_id && monedaCuenta !== form.moneda;
    if (requiereConversion && (!form.tipo_cambio || parseFloat(form.tipo_cambio) <= 0)) {
      toast.error("Falta el tipo de cambio porque la cuenta seleccionada usa otra moneda");
      return;
    }

    // Venta al contado con cobro "a elección": pedir la cuenta antes de guardar.
    if (!isEdit && form.forma_pago !== "credito" && pedirMedioCobro && !cobroConfirmadoRef.current) {
      const unica = cuentasParaCobro.length === 1 ? cuentasParaCobro[0].id : "";
      const pred = cuentasParaCobro.find(c => c.es_predeterminada)?.id || unica || "";
      setCuentaCobroSel(pred);
      setMedioCobroSel(SIN_MEDIO);
      setCobroPicker(true);
      return;
    }

    // Recetario agronómico: si hay productos que requieren receta, pedirla antes de guardar.
    if (!isEdit && !recetaRef.current) {
      const cantPorProd = new Map();
      for (const c of conceptosValidos) {
        if (!c.producto_id) continue;
        cantPorProd.set(String(c.producto_id), (cantPorProd.get(String(c.producto_id)) || 0) + (parseFloat(c.cantidad) || 0));
      }
      const controlados = await verificarControlados(API, token, logoTipo, [...cantPorProd.keys()]);
      if (controlados.length > 0) {
        setRecetaPicker({ items: controlados.map(c => ({ ...c, cantidad: cantPorProd.get(String(c.producto_id)) || 0 })) });
        return;
      }
    }

    const conceptosOut = conceptosValidos.map(c => {
      const cantidad = parseFloat(c.cantidad) || 1;
      const precio = parseFloat(c.precio_unitario) || 0;
      const descuento_pct = Math.min(100, Math.max(0, parseFloat(c.descuento_pct || 0) || 0));
      const subtotal = c.subtotal !== undefined && c.subtotal !== null
        ? Number(c.subtotal)
        : cantidad * precio * (1 - descuento_pct / 100);
      return {
        descripcion: c.descripcion,
        producto_id: form.modo === "productos" ? (c.producto_id || null) : null,
        producto_sku: c.producto_sku || null,
        variante_id: form.modo === "productos" ? (c.variante_id || null) : null,
        variante_texto: c.variante_texto || null,
        cantidad,
        precio_unitario: precio,
        descuento_pct,
        subtotal,
        iva_tipo: c.iva_tipo || "10",
        es_interes: !!c.es_interes,
        // Referencia al renglón del presupuesto (aprobación parcial / facturación por renglón)
        presupuesto_item_ref: c.presupuesto_item_ref || null,
        // Trazabilidad de conversión — cuando el producto viene del catálogo en otra moneda
        // que la factura, precio_unitario ya está convertido; guardamos el original + TC usado.
        moneda_item: c.moneda_item || null,
        tipo_cambio_item: c.moneda_item ? (parseFloat(c.tipo_cambio_item) || null) : null,
        precio_original: c.moneda_item ? (c.precio_original ?? null) : null,
      };
    });
    const montoTotal = conceptosOut.reduce((s, c) => s + c.subtotal, 0);
    const conceptoTexto = conceptosOut.length === 1
      ? conceptosOut[0].descripcion
      : `${conceptosOut.length} ítems`;

    const payload = {
      logo_tipo: form.logo_tipo || logoTipo,
      tipo: form.tipo || "emitida",
      sin_factura: esBoleta,
      numero: esBoleta ? null : form.numero,
      fecha: form.fecha,
      forma_pago: form.forma_pago || "contado",
      razon_social: form.razon_social,
      ruc: form.ruc || null,
      empresa_id: form.empresa_id || form._empresa_id || null,
      empresa_nombre: form.empresa_nombre || null,
      concepto: conceptoTexto,
      conceptos: conceptosOut,
      modo: productosHabilitados ? form.modo : "libre",
      afecta_stock: productosHabilitados && form.modo === "productos" ? (canCambiarAfectaStock ? !!form.afecta_stock : true) : false,
      entrega_posterior: productosHabilitados && form.modo === "productos" && !!form.entrega_posterior,
      deposito_id: depVenta.activo && productosHabilitados && form.modo === "productos" && form.deposito_id ? form.deposito_id : null,
      monto: montoTotal,
      moneda: form.moneda || "PYG",
      tipo_cambio: form.tipo_cambio !== "" && form.tipo_cambio != null ? Number(form.tipo_cambio) : null,
      estado: estadoOut,
      fecha_vencimiento: form.fecha_vencimiento || null,
      plan_cuenta_id: form.plan_cuenta_id || null,
      plan_cuenta_nombre: form.plan_cuenta_nombre || null,
      fecha_pago: estadoOut === "pagada" ? (form.fecha_pago || form.fecha) : null,
      cuenta_id: estadoOut === "pagada" ? (form.cuenta_id || null) : (cuentaCobroRef.current || null),
      cuenta_nombre: estadoOut === "pagada" ? (form.cuenta_nombre || null) : null,
      receta: recetaRef.current || undefined,
      procesadora_id: (!isEdit && medioCobroRef.current.procesadora_id) || undefined,
      metodo_cobro: (!isEdit && medioCobroRef.current.metodo_cobro) || undefined,
      presupuesto_ids: form.presupuesto_ids || [],
      proyecto_constructora_id: form.proyecto_constructora_id || null,
      proyecto_constructora_nombre: form.proyecto_constructora_nombre || null,
      proyecto_cuota_id: form.proyecto_cuota_id || null,
      notas: form.notas || null,
      delivery: tieneDelivery ? {
        repartidor: (form.delivery_repartidor || "").trim(),
        repartidor_id: form.delivery_repartidor_id || null,
        // Solo se guarda si el usuario la cambió a mano; si no, la orden toma la del Catastro (siempre actual).
        direccion: form.delivery_direccion_editada ? (form.delivery_direccion || "").trim() : "",
        referencia: (form.delivery_referencia || "").trim(),
      } : null,
      vendedor_id: form.vendedor_id || null,
      vendedor_nombre: form.vendedor_id
        ? (vendedoresList.find(v => String(v.id) === String(form.vendedor_id))?.nombre || form.vendedor_nombre || null)
        : null,
      descuento_global_monto: form.descuento_global_tipo === "monto" ? (parseFloat(form.descuento_global_monto) || 0) : 0,
      descuento_global_pct: form.descuento_global_tipo === "pct" ? (parseFloat(form.descuento_global_pct) || 0) : 0,
      // Cuotas
      es_credito_cuotas: form.es_credito_cuotas || false,
      num_cuotas: form.es_credito_cuotas ? Number(form.num_cuotas || 1) : null,
      fecha_primer_vencimiento: form.es_credito_cuotas ? (form.fecha_primer_vencimiento || null) : null,
      tasa_interes_cuotas: form.es_credito_cuotas ? Number(form.tasa_interes_cuotas || 0) : null,
      // Timbrado — del punto de expedición seleccionado, solo si hay config activa y NO es boleta
      nro_timbrado: (!esBoleta && selectedPuntoData?.timbrado_activo?.nro_timbrado) || null,
      fecha_inicio_timbrado: (!esBoleta && selectedPuntoData?.timbrado_activo?.fecha_inicio) || null,
      fecha_vigencia_timbrado: (!esBoleta && selectedPuntoData?.timbrado_activo?.tipo_emision !== "electronico" && selectedPuntoData?.timbrado_activo?.fecha_vigencia) || null,
      punto_expedicion: (!esBoleta && form.punto_expedicion) || null,
      // Snapshot del tipo de emisión (y habilitación de autoimpresor, si aplica)
      // vigente en el punto al momento de emitir — el backend igual lo
      // recalcula de forma autoritativa en create_factura, esto es solo para
      // que quede reflejado ya en la respuesta/edición.
      tipo_emision: (!esBoleta && selectedPuntoData?.timbrado_activo?.tipo_emision) || null,
      habilitacion_autoimpresor: (!esBoleta && selectedPuntoData?.timbrado_activo?.tipo_emision === "autoimpresor" && timbradoConfig?.habilitacion_autoimpresor) || null,
    };

    cuentaCobroRef.current = "";
    medioCobroRef.current = SIN_MEDIO;
    cobroConfirmadoRef.current = false;
    recetaRef.current = null;
    try {
      setSaving(true);

      // El cliente ya se selecciona o se crea explícitamente desde el buscador
      // (ClienteSearchSelect, arriba) antes de llegar acá — payload.empresa_id
      // siempre viene resuelto, ya no hace falta un auto-registro silencioso al guardar.

      const url = isEdit
        ? `${API}/admin/facturas/${factura.id}`
        : `${API}/admin/facturas`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const savedData = await res.json().catch(() => null);
      if (payload.receta && savedData?.id && hasPermission?.("recetario.imprimir")) {
        imprimirRecetaDeFactura(API, token, logoTipo, savedData.id, activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "");
      }
      toast.success(isEdit ? (esBoleta ? "Boleta actualizada" : "Factura actualizada") : (esBoleta ? "Boleta creada" : "Factura creada"));
      // Si esta factura salió de una tarjeta de comanda (Restaurante), liberamos
      // la tarjeta tras el cobro exitoso — best-effort, no bloquea ni revierte
      // la factura ya guardada si esto falla.
      if (!isEdit && comandaAbierta?.id) {
        try {
          await fetch(`${API}/admin/restaurante/comandas/${comandaAbierta.id}/liberar`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          });
        } catch (_e) {
          // silencioso — la factura ya se concretó
        }
      }
      // Auto-impresión solo al CREAR (no en ediciones) y solo si hay un formato configurado
      // en Ventas > Configuración > Impresión al guardar factura (empresa o por usuario).
      if (onSaved) onSaved(savedData, { autoPrint: !isEdit && imprimirAlGuardar, autoPrintModo: !isEdit ? autoPrintModo : "no" });
    } catch (e) {
      toast.error(e.message || "No se pudo guardar la factura");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  const inputCls =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white";
  const inputReadonlyCls =
    "w-full border border-gray-100 rounded-lg px-3 py-2 text-sm text-gray-600 bg-gray-50 cursor-not-allowed";
  const labelCls = "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block";

  return (
    <>
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-6 px-4"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${esBoleta ? "bg-violet-600" : "bg-blue-600"}`}>
              {esBoleta ? <Receipt className="w-5 h-5 text-white" /> : <FileText className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {isEdit
                  ? (esBoleta ? `Editar Boleta ${factura.numero_boleta || factura.numero}` : `Editar Factura ${factura.numero}`)
                  : (esBoleta ? "Venta sin Factura (Boleta)" : "Nueva Factura")}
              </h2>
              <p className="text-xs text-gray-400">
                {esBoleta
                  ? "Venta sin comprobante fiscal — el número de boleta se genera automáticamente"
                  : (isEdit ? "Modificar datos de la factura" : "Crear nueva factura")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* ── Boleta notice ── */}
          {esBoleta && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-center gap-3 text-xs text-violet-700">
              <Receipt className="w-4 h-4 flex-shrink-0" />
              <span>Esta venta <strong>no genera comprobante fiscal</strong>. No afecta al IVA. El número de boleta se asignará automáticamente al guardar.</span>
            </div>
          )}

          {/* ── Timbrado Banner (solo para facturas normales) ── */}
          {!esBoleta && (
            loadingTimbrado ? (
              <div className="flex items-center gap-2 text-gray-400 text-xs py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verificando timbrado...
              </div>
            ) : timbradoConfig ? (
              <div className="space-y-2">
                {puntosExpedicion.length > 1 && (
                  <div>
                    <label className={labelCls}>Punto de expedición</label>
                    <select
                      className={inputCls}
                      value={form.punto_expedicion}
                      disabled={puntoBloqueado && !isEdit}
                      title={puntoBloqueado && !isEdit ? "Tu usuario tiene un punto de expedición asignado" : undefined}
                      onChange={(e) => setForm(prev => ({ ...prev, punto_expedicion: e.target.value, numero: "" }))}
                    >
                      {puntosExpedicion.filter(p => !(puntoBloqueado && !isEdit) || p.codigo === timbradoConfig?.punto_sugerido).map(p => (
                        <option key={p.codigo} value={p.codigo}>
                          Punto {p.codigo}{p.descripcion ? ` — ${p.descripcion}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
                  timbradoVigente
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}>
                  <Shield className={`w-4 h-4 mt-0.5 flex-shrink-0 ${timbradoVigente ? "text-emerald-600" : "text-red-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${timbradoVigente ? "text-emerald-800" : "text-red-700"}`}>
                      {timbradoVigente ? "Timbrado vigente" : selectedPuntoData?.timbrado_activo ? "⚠️ Timbrado vencido — no se pueden emitir nuevas facturas" : "⚠️ Este punto no tiene timbrado configurado"}
                    </p>
                    {selectedPuntoData?.timbrado_activo && (
                      <p className={`text-[11px] mt-0.5 ${timbradoVigente ? "text-emerald-600" : "text-red-500"}`}>
                        N° {selectedPuntoData.timbrado_activo.nro_timbrado} · {selectedPuntoData.timbrado_activo.tipo_emision === "electronico" ? "Electrónico sin vencimiento" : `Vigencia: ${selectedPuntoData.timbrado_activo.fecha_inicio} → ${selectedPuntoData.timbrado_activo.fecha_vigencia}`}
                        {selectedPuntoData.timbrado_activo.establecimiento && ` · Est. ${selectedPuntoData.timbrado_activo.establecimiento}`}
                      </p>
                    )}
                  </div>
                  {hasPermission?.("configuracion_ventas.gestionar_timbrado") && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      <Settings className="w-3 h-3 inline-block mr-0.5" />Configurar en Ventas &gt; Configuración
                    </span>
                  )}
                </div>
              </div>
            ) : hasPermission?.("configuracion_ventas.gestionar_timbrado") ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3 text-xs text-amber-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Sin timbrado configurado. Podés configurarlo en <strong className="mx-1">Ventas &gt; Configuración &gt; Timbrado fiscal</strong>.
              </div>
            ) : null
          )}

          {/* Row 1: Número (oculto en boleta), Fecha */}
          <div className={`grid gap-4 ${esBoleta ? "grid-cols-1" : "grid-cols-2"}`}>
            {!esBoleta && (
              <div>
                <label className={labelCls}>Número *</label>
                {!isEdit && esAutoNumerico ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        className={inputCls + " flex-1 bg-gray-50 text-gray-500"}
                        value={form.numero || ""}
                        readOnly
                        placeholder="Número auto-generado"
                      />
                      <button
                        type="button"
                        disabled={loadingNumero || !timbradoVigente}
                        onClick={() => fetchSiguienteNumero(form.punto_expedicion || puntosExpedicion[0]?.codigo || "001")}
                        className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                      >
                        {loadingNumero ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Generar N°"}
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400">El número se genera automáticamente y reserva el correlativo.</p>
                  </div>
                ) : (
                  <input
                    className={inputCls}
                    value={form.numero}
                    onChange={(e) => set("numero", e.target.value)}
                    placeholder="001-001-0001234"
                  />
                )}
              </div>
            )}
            <div>
              <label className={labelCls}>Fecha *</label>
              <DateInput
                className={inputCls}
                value={form.fecha}
                onChange={(e) => set("fecha", e.target.value)}
              />
            </div>
          </div>

          {/* Row 2: Forma pago, Plan de cuenta y vencimiento/cuotas */}
          <div className={`grid gap-4 ${form.es_credito_cuotas ? "grid-cols-2" : "grid-cols-3"}`}>
            <div>
              <label className={labelCls}>Forma de Pago</label>
              <select
                className={inputCls}
                value={form.forma_pago}
                onChange={(e) => setForm(prev => ({ ...prev, forma_pago: e.target.value, plan_cuenta_id: "", plan_cuenta_nombre: "", fecha_vencimiento: "", es_credito_cuotas: false }))}
              >
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Plan de cuenta *</label>
              <select
                className={inputCls}
                value={form.plan_cuenta_id || ""}
                onChange={(e) => aplicarPlanCuenta(e.target.value, true)}
              >
                <option value="">Seleccionar cuenta...</option>
                {planCuentasFiltradas.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}{c.es_cuotas ? " 📋" : ""}{c.predeterminada ? " ★" : ""}
                  </option>
                ))}
              </select>
            </div>
            {!form.es_credito_cuotas && (
              <div>
                <label className={labelCls}>Vencimiento</label>
                <DateInput
                  className={inputCls}
                  value={form.fecha_vencimiento}
                  onChange={(e) => set("fecha_vencimiento", e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Cuotas panel — visible when selected plan cuenta has es_cuotas */}
          {form.es_credito_cuotas && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-3">
              <p className="text-xs font-semibold text-violet-700 flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" /> Configuración de cuotas
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>N° cuotas</label>
                  <select
                    className={inputCls}
                    value={form.num_cuotas}
                    onChange={e => setForm(prev => ({ ...prev, num_cuotas: Number(e.target.value) }))}
                  >
                    {Array.from({ length: planCuentaSeleccionada?.mi_max_cuotas || planCuentaSeleccionada?.max_cuotas || 6 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} cuota{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>1° vencimiento</label>
                  <DateInput
                    className={inputCls}
                    value={form.fecha_primer_vencimiento}
                    onChange={e => setForm(prev => ({ ...prev, fecha_primer_vencimiento: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Interés (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={form.tasa_interes_cuotas}
                    onChange={e => setForm(prev => ({ ...prev, tasa_interes_cuotas: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              {cuotasPreview.length > 0 && (
                <div className="text-xs text-violet-700 space-y-1">
                  <p className="font-medium">Vista previa de cuotas:</p>
                  <div className="flex flex-wrap gap-2">
                    {cuotasPreview.map(c => (
                      <span key={c.num} className="px-2 py-0.5 rounded bg-violet-100 border border-violet-200">
                        Cuota {c.num}: {form.moneda === "USD" ? `USD ${c.monto.toLocaleString("es-PY")}` : `₲ ${c.monto.toLocaleString("es-PY")}`}
                      </span>
                    ))}
                  </div>
                  {Number(form.tasa_interes_cuotas) > 0 && !(form.conceptos || []).some(c => c.es_interes) && (
                    <p className="text-violet-500">
                      La factura se emitirá con una línea de intereses (exenta) · Total con {form.tasa_interes_cuotas}% interés: {form.moneda === "USD" ? "USD" : "₲"} {Math.round(montoTotalActual * (1 + Number(form.tasa_interes_cuotas) / 100)).toLocaleString("es-PY")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Row 3: Moneda, Tipo de Cambio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Moneda</label>
              <select
                className={inputCls}
                value={form.moneda}
                onChange={(e) => set("moneda", e.target.value)}
              >
                <option value="PYG">Guaraníes (₲)</option>
                <option value="USD">Dólares (USD)</option>
                {activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") && (
                  <option value="BRL">Reales Brasileños (R$)</option>
                )}
              </select>
            </div>
            {form.moneda !== "PYG" && (
              <div>
                <label className={labelCls}>Tipo de Cambio (1 {form.moneda} = X ₲)</label>
                <input
                  type="number"
                  className={inputCls}
                  value={form.tipo_cambio}
                  onChange={(e) => { setTcInfo(null); set("tipo_cambio", e.target.value); }}
                  placeholder="7500"
                />
                {form.moneda === "USD" && tcInfo && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    Sugerido DNIT venta: ₲ {Number(tcInfo.tipo_cambio_sugerido || tcInfo.venta).toLocaleString("es-PY")} ({tcInfo.fecha})
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Separator: Datos del Cliente */}
          <div className="border-t border-dashed border-gray-200 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Datos del Cliente
            </p>

            {/* Buscador de cliente — mismo patrón que el selector de proveedor en Compras */}
            <div className="mb-4">
              <label className={labelCls}>Cliente *</label>
              {defaultEmpresaId && !form._empresa_id ? (
                <div className={inputReadonlyCls}>Cargando cliente...</div>
              ) : (
                <ClienteSearchSelect
                  empresas={defaultEmpresaId ? empresasLocal.filter(e => String(e.id) === String(defaultEmpresaId)) : empresasLocal}
                  value={form._empresa_id ? { id: form._empresa_id, nombre: form.empresa_nombre || form.razon_social, razon_social: form.razon_social, ruc: form.ruc } : null}
                  onSelect={applyEmpresaSeleccionada}
                  onCreated={handleClienteCreado}
                  token={token}
                  logo={logoTipo}
                />
              )}
              {form._empresa_id && (
                <p className="text-xs text-blue-500 mt-1">
                  Datos de cliente cargados. Presupuestos filtrados.
                </p>
              )}
            </div>

            {/* Vendedor — automático (quien carga la venta). En edición, solo un usuario
                con el permiso granular "reasignar vendedor" puede corregirlo; queda
                registrado en el historial/auditoría de la factura. */}
            {(isEdit ? !!form.vendedor_nombre : vendedoresList.length > 0) && (
              <div className="mb-4">
                <label className={labelCls}>Vendedor</label>
                {(isEdit ? (canReasignarVendedor && vendedoresList.length > 0) : true) ? (
                  <select
                    className={inputCls}
                    value={form.vendedor_id || ""}
                    onChange={(e) => {
                      const vid = e.target.value;
                      const v = vendedoresList.find(x => String(x.id) === String(vid));
                      setForm(prev => ({ ...prev, vendedor_id: vid, vendedor_nombre: v?.nombre || (isEdit ? prev.vendedor_nombre : "") }));
                    }}
                  >
                    {!isEdit && <option value="">— Yo (usuario actual) —</option>}
                    {isEdit && form.vendedor_id && !vendedoresList.some(v => String(v.id) === String(form.vendedor_id)) && (
                      <option value={form.vendedor_id}>{form.vendedor_nombre}</option>
                    )}
                    {vendedoresList.map(v => (
                      <option key={v.id} value={v.id}>{v.nombre} ({Number(v.porcentaje_comision || 0).toFixed(1)}%)</option>
                    ))}
                  </select>
                ) : (
                  <div className={inputReadonlyCls}>{form.vendedor_nombre}</div>
                )}
              </div>
            )}

            {tieneDelivery && (
              <div className="mb-4 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4">
                <p className="text-orange-300 text-xs font-semibold mb-3">Delivery — datos de entrega</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Repartidor</label>
                    {repartidores.length > 0 ? (
                      <select className={inputCls} value={form.delivery_repartidor_id || ""}
                        onChange={e => {
                          const r = repartidores.find(x => x.id === e.target.value);
                          setForm(prev => ({ ...prev, delivery_repartidor_id: e.target.value, delivery_repartidor: r?.nombre || "" }));
                        }}>
                        <option value="">— Elegir repartidor —</option>
                        {form.delivery_repartidor_id && !repartidores.some(x => x.id === form.delivery_repartidor_id) && (
                          <option value={form.delivery_repartidor_id}>{form.delivery_repartidor}</option>
                        )}
                        {repartidores.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                      </select>
                    ) : (
                      <input className={inputCls} value={form.delivery_repartidor || ""} onChange={e => setForm(prev => ({ ...prev, delivery_repartidor: e.target.value }))} placeholder="Nombre del delivery (o creá entidades con rol Repartidor en el Catastro)" />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Dirección de entrega</label>
                    <input className={inputCls}
                      value={form.delivery_direccion_editada ? (form.delivery_direccion || "") : clienteDireccionDefault}
                      onChange={e => setForm(prev => ({ ...prev, delivery_direccion: e.target.value, delivery_direccion_editada: true }))}
                      placeholder="Dirección del cliente (Catastro)" />
                  </div>
                  <div>
                    <label className={labelCls}>Referencia de entrega</label>
                    <input className={inputCls} value={form.delivery_referencia || ""} onChange={e => setForm(prev => ({ ...prev, delivery_referencia: e.target.value }))} placeholder="Ej: portón verde, frente al supermercado" />
                  </div>
                </div>
                <p className="text-slate-500 text-[11px] mt-2">Si dejás la dirección vacía se usa la del cliente en el Catastro. La orden de delivery se imprime con los datos de tu empresa y del cliente.</p>
              </div>
            )}

            {isEdit && !form._empresa_id ? (
              // Factura antigua sin cliente vinculado al Catastro (de cuando estos campos
              // se cargaban a mano) — se deja editable para no bloquear la corrección de
              // registros históricos. Toda factura nueva pasa siempre por el buscador de arriba.
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Razón Social *</label>
                    <input
                      className={inputCls}
                      value={form.razon_social}
                      onChange={(e) => set("razon_social", e.target.value)}
                      placeholder="Empresa S.A."
                    />
                  </div>
                  <div>
                    <label className={labelCls}>RUC</label>
                    <input
                      className={inputCls}
                      value={form.ruc}
                      onChange={(e) => set("ruc", e.target.value)}
                      placeholder="80123456-7"
                    />
                  </div>
                </div>
                <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                  <span>Factura histórica sin cliente vinculado. Para asociarla al Catastro, seleccioná o creá un cliente en el buscador de arriba.</span>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Razón Social</label>
                  <div className={inputReadonlyCls}>
                    {form.razon_social || <span className="text-gray-300">Seleccioná o creá un cliente arriba</span>}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>RUC</label>
                  <div className={inputReadonlyCls}>
                    {form.ruc || <span className="text-gray-300">Sin RUC</span>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Conceptos */}
          <div className="border-t border-dashed border-gray-200 pt-4">
            {comandaAbierta && (
              <div className="mb-3 px-4 py-2 rounded-lg bg-orange-50 border border-orange-300 text-orange-800 text-sm flex items-center justify-between">
                <span>Comanda cargada: <strong>{comandaAbierta.mesa_nombre || comandaAbierta.numero}</strong> — el consumo de la tarjeta se agregó a esta factura.</span>
                <button type="button" onClick={() => setComandaAbierta(null)} className="text-orange-600 hover:text-orange-900 text-xs underline">Quitar referencia</button>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Conceptos / Ítems
              </p>
              <button
                onClick={addConcepto}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Agregar ítem
              </button>
            </div>

            {(productosHabilitados || productos.length > 0) && (
              <div className={`mb-3 flex flex-wrap items-end gap-2 p-3 rounded-xl border ${devolucionActiva ? "border-red-300 bg-red-50/80" : "border-emerald-200 bg-emerald-50/80"}`}>
                <div className="flex-1 min-w-[200px]">
                  <label className={labelCls}>Código de barras {devolucionActiva && "(modo Devolución)"}</label>
                  <input
                    className={inputCls}
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleBarcodeSubmit(); } }}
                    placeholder="Escanear o escribir y Enter"
                    disabled={barcodeLoading}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleBarcodeSubmit}
                  disabled={barcodeLoading || !barcodeInput.trim()}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${devolucionActiva ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                >
                  {barcodeLoading ? "Buscando..." : (devolucionActiva ? "Restar" : "Agregar")}
                </button>
                <p className={`w-full text-[11px] ${devolucionActiva ? "text-red-700" : "text-emerald-700"}`}>
                  {devolucionActiva
                    ? "Cada escaneo resta 1 unidad del ítem coincidente. Si llega a 0, se quita de la factura."
                    : "Si el producto ya está en la lista, se suma 1 a la cantidad."}
                </p>
              </div>
            )}

            {(productosHabilitados || productos.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {productosHabilitados && canModoLibre && (
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, modo: "libre", afecta_stock: false, entrega_posterior: false }))}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.modo === "libre" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:border-blue-300"}`}
                  >
                    Libre
                  </button>
                )}
                {productosHabilitados && (
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, modo: "productos", afecta_stock: true }))}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.modo === "productos" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-500 border-gray-200 hover:border-emerald-300"}`}
                  >
                    Catálogo
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDevolucionActiva(prev => !prev)}
                  title="Al activarlo, cada escaneo resta 1 a la cantidad del ítem en vez de sumar"
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${devolucionActiva ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-500 border-gray-200 hover:border-red-300"}`}
                >
                  Devolución
                </button>
                {productosHabilitados && form.modo === "productos" && (
                  <button
                    type="button"
                    onClick={() => canCambiarAfectaStock && setForm(prev => ({ ...prev, afecta_stock: !prev.afecta_stock }))}
                    disabled={!canCambiarAfectaStock}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.afecta_stock ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"}`}
                    title={canCambiarAfectaStock ? "Cambiar afectación de stock" : "Siempre afecta stock con catálogo"}
                  >
                    {form.afecta_stock ? "Afecta stock" : "No afecta stock"}
                  </button>
                )}
                {productosHabilitados && form.modo === "productos" && form.afecta_stock && depVenta.activo && depVenta.depositos.length > 0 && (
                  <select
                    value={form.deposito_id || ""}
                    onChange={e => setForm(prev => ({ ...prev, deposito_id: e.target.value }))}
                    title="Depósito del que sale la mercadería de esta factura. Vacío = el depósito de ventas configurado para tu usuario."
                    className="px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-700"
                  >
                    <option value="">Depósito: predeterminado</option>
                    {depVenta.depositos.map(d => <option key={d.id} value={d.id}>Depósito: {d.codigo} · {d.nombre}</option>)}
                  </select>
                )}
                {productosHabilitados && form.modo === "productos" && form.afecta_stock && (
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, entrega_posterior: !prev.entrega_posterior }))}
                    title="Se factura sin descontar stock (aunque aún no esté cargado). La mercadería se entrega después desde Ventas → Entregas pendientes y el stock baja recién ahí: hace falta stock suficiente al entregar. Solo aplica a productos del catálogo (no a libres ni servicios)."
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.entrega_posterior ? "bg-violet-600 text-white border-violet-600" : "bg-white text-gray-500 border-gray-200 hover:border-violet-300"}`}
                  >
                    {form.entrega_posterior ? "Entrega posterior ✓" : "Entrega posterior"}
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-400 px-1">
                <div className={canUseDescuento ? "col-span-5" : "col-span-6"}>Descripción</div>
                <div className="col-span-2 text-center">Cant.</div>
                <div className="col-span-2 text-right">P. Unitario</div>
                {canUseDescuento && <div className="col-span-1 text-right">Desc.%</div>}
                <div className="col-span-1 text-right">Subtotal</div>
                <div className="col-span-1"></div>
              </div>

              {form.conceptos.map((c, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <div className={`${canUseDescuento ? "col-span-5" : "col-span-6"} min-w-0`}>
                    {productosHabilitados && form.modo === "productos" && (
                      <div className="mb-1 min-w-0">
                        <ProductoSearchSelect
                          productos={productos.filter(p => p.activo !== false && (!p.logo_tipo || p.logo_tipo === logoTipo))}
                          value={c.producto_id || ""}
                          variantValue={c.variante_id || ""}
                          includeVariants={true}
                          onChange={(pid, prod, variante) => selectProductoForConcepto(idx, pid, prod, variante)}
                          placeholder="Buscar producto..."
                          theme="light"
                        />
                      </div>
                    )}
                    <input
                      className={inputCls + (form.modo === "productos" ? " cursor-default opacity-70" : "")}
                      value={c.descripcion}
                      readOnly={form.modo === "productos"}
                      onChange={(e) => { if (form.modo !== "productos") handleConceptoChange(idx, "descripcion", e.target.value); }}
                      placeholder={form.modo === "productos" ? "Se completa al seleccionar producto" : "Descripción del ítem"}
                    />
                    {/* SKU badge cuando viene del catálogo */}
                    {c.producto_sku && (
                      <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">SKU: {c.producto_sku}</span>
                    )}
                    {c.variante_texto && (
                      <span className="inline-flex max-w-full items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] text-emerald-700 mt-1">
                        <span className="truncate">Variante: {c.variante_texto}</span>
                      </span>
                    )}
                    {/* IVA tipo — editable en modo libre, solo lectura en catálogo */}
                    {!esBoleta && (
                      <div className="flex gap-1 mt-1">
                        {[["exenta","Exenta"],["5","5%"],["10","10%"]].map(([v, l]) => {
                          const isActive = (c.iva_tipo || "10") === v;
                          const fromCatalog = !!c.producto_id;
                          return (
                            <button key={v} type="button"
                              onClick={() => !fromCatalog && handleConceptoChange(idx, "iva_tipo", v)}
                              title={fromCatalog ? "El IVA viene definido por el producto" : undefined}
                              className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors ${
                                isActive
                                  ? v === "exenta"
                                    ? "bg-slate-500/30 border-slate-400/50 text-slate-300"
                                    : v === "5"
                                    ? "bg-amber-500/25 border-amber-400/50 text-amber-300"
                                    : "bg-blue-500/25 border-blue-400/50 text-blue-300"
                                  : "bg-white/5 border-white/10 text-gray-400"
                              } ${fromCatalog ? "cursor-default opacity-70" : "hover:border-gray-400 cursor-pointer"}`}>
                              {l}
                            </button>
                          );
                        })}
                        {!!c.producto_id && (
                          <span className="text-[9px] text-slate-500 self-center ml-1">🔒 del producto</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      className={inputCls + " text-center"}
                      value={c.cantidad}
                      onChange={(e) => handleConceptoChange(idx, "cantidad", e.target.value)}
                      min="0"
                      step="any"
                    />
                  </div>
                  <div className="col-span-2">
                    <MoneyInput
                      className={inputCls + " text-right" + (isPrecioLocked(c) ? " opacity-60 cursor-not-allowed" : "")}
                      value={c.precio_unitario}
                      moneda={form.moneda}
                      readOnly={isPrecioLocked(c)}
                      onChange={(value) => handlePrecioChange(idx, value)}
                      onBlur={() => handlePrecioBlur(idx, c.precio_unitario)}
                      title={isPrecioLocked(c) ? "Sin permiso para modificar el precio del catálogo" : undefined}
                    />
                    {/* Indicador de precio modificado */}
                    {!!c.producto_id && form.modo === "productos" && c.precio_catalogo != null && parseFloat(c.precio_unitario) !== parseFloat(c.precio_catalogo) && (
                      <span className={`text-[9px] font-medium block text-right mt-0.5 ${parseFloat(c.precio_unitario) < parseFloat(c.precio_catalogo) ? "text-red-400" : "text-emerald-400"}`}>
                        {parseFloat(c.precio_unitario) < parseFloat(c.precio_catalogo) ? "▼ con descuento" : "▲ por encima"}
                      </span>
                    )}
                    {isPrecioLocked(c) && (
                      <span className="text-[9px] text-slate-400 block text-right mt-0.5">🔒 precio fijo</span>
                    )}
                    {/* Producto cargado en el catálogo en otra moneda — TC de conversión editable */}
                    {c.moneda_item && c.moneda_item !== form.moneda && (
                      <div className="mt-1">
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] text-amber-400 whitespace-nowrap">
                            {c.precio_original != null ? `${c.moneda_item} ${Number(c.precio_original).toLocaleString("es-PY", { minimumFractionDigits: 2 })} · TC` : "TC"}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={c.tipo_cambio_item ?? ""}
                            onChange={(e) => handleTipoCambioItemChange(idx, e.target.value)}
                            placeholder={form.tipo_cambio ? String(form.tipo_cambio) : "TC"}
                            className="w-full bg-transparent border border-amber-500/40 rounded px-1 py-0.5 text-[10px] text-amber-200 text-right focus:outline-none focus:border-amber-500/70"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {canUseDescuento && (
                    <div className="col-span-1">
                      <input
                        type="number"
                        className={inputCls + " text-right"}
                        value={c.descuento_pct ?? 0}
                        onChange={(e) => handleConceptoChange(idx, "descuento_pct", e.target.value)}
                        min="0"
                        max="100"
                        step="0.5"
                        title="Descuento %"
                        placeholder="0"
                      />
                      {(parseFloat(c.descuento_pct) > 0) && (
                        <span className="text-[9px] text-emerald-500 block text-right mt-0.5">−{parseFloat(c.descuento_pct)}%</span>
                      )}
                    </div>
                  )}
                  <div className="col-span-1 text-right text-xs font-mono text-gray-600 px-1">
                    {fmtMonto(c.subtotal)}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {form.conceptos.length > 1 && (
                      <button
                        onClick={() => removeConcepto(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Descuento Global */}
              {puedeDescuentoGlobal && (
                <div className="mt-2 flex flex-wrap items-end gap-2 p-3 rounded-xl border border-blue-200 bg-blue-50/60">
                  <div className="w-36">
                    <label className={labelCls}>Descuento global</label>
                    <select
                      className={inputCls}
                      value={form.descuento_global_tipo}
                      onChange={(e) => setForm(prev => ({ ...prev, descuento_global_tipo: e.target.value }))}
                    >
                      <option value="monto">Monto ({form.moneda === "USD" ? "US$" : form.moneda === "BRL" ? "R$" : "₲"})</option>
                      <option value="pct">Porcentaje (%)</option>
                    </select>
                  </div>
                  <div className="w-36">
                    {form.descuento_global_tipo === "pct" ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        className={inputCls}
                        value={form.descuento_global_pct}
                        onChange={(e) => setForm(prev => ({ ...prev, descuento_global_pct: e.target.value }))}
                        placeholder="Ej: 10"
                      />
                    ) : (
                      <MoneyInput
                        value={form.descuento_global_monto}
                        moneda={form.moneda}
                        onChange={(value) => setForm(prev => ({ ...prev, descuento_global_monto: value }))}
                        className={inputCls}
                        placeholder="0"
                      />
                    )}
                  </div>
                  {descuentoGlobalMonto > 0 && (
                    <span className="text-[11px] text-emerald-600 pb-2">
                      −{fmtMonto(descuentoGlobalMonto)} aplicado sobre el total
                    </span>
                  )}
                </div>
              )}

              {/* Total + IVA breakdown */}
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <div className="bg-gray-50 rounded-xl px-4 py-2 text-right space-y-0.5 min-w-[220px]">
                  {!esBoleta && ivaBreakdown.exenta > 0 && (
                    <p className="text-xs text-gray-400">Exento: {fmtMonto(ivaBreakdown.exenta)}</p>
                  )}
                  {!esBoleta && ivaBreakdown.iva5 > 0 && (
                    <p className="text-xs text-amber-600">IVA 5% incluido: {fmtMonto(ivaBreakdown.iva5)}</p>
                  )}
                  {!esBoleta && ivaBreakdown.iva10 > 0 && (
                    <p className="text-xs text-blue-600">IVA 10% incluido: {fmtMonto(ivaBreakdown.iva10)}</p>
                  )}
                  {descuentoGlobalMonto > 0 && (
                    <p className="text-xs text-emerald-600">Descuento global: −{fmtMonto(descuentoGlobalMonto)}</p>
                  )}
                  <p className="text-xs text-gray-400 mb-0.5 pt-1 border-t border-gray-200">Total factura</p>
                  <p className="text-lg font-bold text-gray-800">{fmtMonto(totalMonto)}</p>
                  {form.moneda !== "PYG" && form.tipo_cambio && (
                    <p className="text-xs text-gray-400">
                      ≈ ₲ {Math.round(totalMonto * parseFloat(form.tipo_cambio)).toLocaleString("es-PY")}
                    </p>
                  )}
                  {!esBoleta && totalMonto > 0 && (
                    <p className="text-[10px] text-gray-400 italic pt-1 border-t border-gray-200 leading-tight">
                      Son: {totalEnLetras}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Vincular Presupuestos */}
          {presupuestosFiltrados.length > 0 && (
            <div className="border-t border-dashed border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Vincular Presupuestos
                  {form._empresa_id && <span className="ml-1 text-blue-400 normal-case font-normal">(filtrado por empresa)</span>}
                </p>
                <span className="text-[11px] text-gray-400">{presupuestosFiltrados.length} total</span>
              </div>

              {/* Buscador */}
              <div className="relative mb-2">
                <input
                  type="text"
                  value={presSearch}
                  onChange={e => setPresSearch(e.target.value)}
                  placeholder="Buscar por número, nombre, monto…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-400"
                />
                <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {presSearch && (
                  <button onClick={() => setPresSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs leading-none">✕</button>
                )}
              </div>

              {(() => {
                const selIds = form.presupuesto_ids;
                const q = presSearch.trim().toLowerCase();
                const matchSearch = (p) => {
                  if (!q) return true;
                  return (
                    String(p.numero || "").toLowerCase().includes(q) ||
                    (p.nombre_archivo || "").toLowerCase().includes(q) ||
                    String(Math.round(p.total) || "").includes(q) ||
                    (p.empresa_nombre || "").toLowerCase().includes(q)
                  );
                };
                // Seleccionados siempre arriba aunque no coincidan con la búsqueda
                const libres = presupuestosFiltrados.filter(p =>
                  (selIds.includes(p.id) || (p.estado !== "facturado" && p.estado !== "cobrado")) && matchSearch(p)
                );
                const yaFacturados = presupuestosFiltrados.filter(p =>
                  !selIds.includes(p.id) && (p.estado === "facturado" || p.estado === "cobrado") && matchSearch(p)
                );
                const renderBtn = (p) => {
                  const selected = selIds.includes(p.id);
                  const monFmt = p.moneda === "USD"
                    ? `USD ${Number(p.total).toLocaleString("es-PY", { minimumFractionDigits: 2 })}`
                    : `₲ ${Math.round(p.total).toLocaleString("es-PY")}`;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePresupuesto(p.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs border transition-all flex items-center justify-between gap-2 ${
                        selected
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <span>
                        <span className="font-mono font-semibold">#{p.numero}</span>
                        {p.nombre_archivo && (
                          <span className={selected ? " text-blue-200" : " text-gray-400"}> · {p.nombre_archivo.slice(0, 30)}</span>
                        )}
                      </span>
                      <span className={`whitespace-nowrap text-[11px] ${selected ? "text-blue-200" : "text-gray-400"}`}>{monFmt}</span>
                    </button>
                  );
                };
                return (
                  <div className="max-h-56 overflow-y-auto pr-0.5 border border-gray-100 rounded-lg bg-gray-50">
                    {libres.length === 0 && yaFacturados.length === 0 && (
                      <p className="text-xs text-gray-400 py-3 text-center">
                        {q ? "Sin resultados para esa búsqueda" : "No hay presupuestos disponibles"}
                      </p>
                    )}
                    {libres.length > 0 && (
                      <>
                        <div className="px-2 pt-2 pb-1">
                          <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">
                            Pendientes / Disponibles ({libres.length})
                          </span>
                        </div>
                        <div className="px-2 pb-1 space-y-1">
                          {libres.map(renderBtn)}
                        </div>
                      </>
                    )}
                    {yaFacturados.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 px-2 pt-2 pb-1">
                          <div className="flex-1 border-t border-gray-200" />
                          <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide whitespace-nowrap">
                            Ya vinculados a una factura ({yaFacturados.length})
                          </span>
                          <div className="flex-1 border-t border-gray-200" />
                        </div>
                        <div className="px-2 pb-2 space-y-1">
                          {yaFacturados.map(renderBtn)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {form.presupuesto_ids.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <p className="text-xs text-blue-500">
                    {form.presupuesto_ids.length} presupuesto(s) vinculado(s)
                  </p>
                  <button
                    type="button"
                    onClick={cargarItemsPresupuestos}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                  >
                    <Package className="w-3.5 h-3.5" />
                    Cargar ítems del presupuesto
                  </button>
                </div>
              )}
            </div>
          )}


          {/* Notas */}
          <div className="border-t border-dashed border-gray-200 pt-4">
            <label className={labelCls}>Notas / Observaciones</label>
            <textarea
              className={inputCls + " min-h-[70px] resize-none"}
              value={form.notas}
              onChange={(e) => set("notas", e.target.value)}
              placeholder="Observaciones adicionales..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (!isEdit && timbradoConfig && !timbradoVigente)}
            title={!isEdit && timbradoConfig && !timbradoVigente ? "Timbrado vencido — renovalo antes de emitir" : undefined}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg shadow transition-all"
          >
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : isEdit ? "Guardar Cambios" : (imprimirAlGuardar ? "Guardar e Imprimir" : "Crear Factura")}
          </button>
        </div>
      </div>
    </div>

    {/* Selector rápido de variante al escanear un código de barras compartido
        por más de una variante activa (mismo talle/color agrupados bajo un
        único código de barras a nivel de producto). */}
    {varianteScanPending && (
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      >
        <div
          className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Elegí la variante</h3>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">
                {varianteScanPending.prod.nombre}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setVarianteScanPending(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {varianteScanPending.variantesActivas.map((v) => {
              const stock = v.stock_actual ?? 0;
              const stockBajo = stock <= 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={varianteScanPending.modo === "agregar" && stockBajo}
                  onClick={() => handleVarianteScanSelect(v)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors mb-1 last:mb-0 ${
                    varianteScanPending.modo === "agregar" && stockBajo
                      ? "opacity-50 cursor-not-allowed bg-gray-50"
                      : "hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
                  }`}
                >
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {v.atributos_texto || "Variante"}
                  </span>
                  <span className={`text-xs font-semibold whitespace-nowrap ${stockBajo ? "text-red-500" : "text-emerald-600"}`}>
                    Stock: {stock}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <p className="text-[11px] text-gray-400">
              {varianteScanPending.modo === "restar"
                ? "Se restará 1 unidad de la variante elegida."
                : "Se agregará 1 unidad de la variante elegida."}
            </p>
          </div>
        </div>
      </div>
    )}
    <RecetaFormModal
      open={!!recetaPicker} onClose={() => setRecetaPicker(null)} token={token} logoTipo={logoTipo}
      cliente={{ id: form._empresa_id, nombre: form.razon_social, ruc: form.ruc }}
      items={recetaPicker?.items || []}
      title="Receta agronómica — obligatoria para esta venta"
      confirmLabel="Confirmar receta y guardar"
      puedeCrearIngeniero={!!hasPermission?.("recetario.gestionar_ingenieros")}
      onConfirm={async (payload) => { recetaRef.current = payload; setRecetaPicker(null); handleSave(); }}
    />
    {cobroPicker && (
      <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 px-4">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-gray-900">Cobro de la venta al contado</h3>
          <p className="text-sm text-gray-500 mt-1">Elegí cómo se cobra. Quedará registrada como pagada.</p>
          <div className="mt-3">
            <MedioCobroSelector theme="light" procesadoras={procesadorasCobro} value={medioCobroSel} onChange={setMedioCobroSel}
              monto={montoTotalActual}
              fmt={(n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 2 })} />
          </div>
          {(cobroAEleccion || cuentasParaCobro.length > 1) && (
            cuentasParaCobro.length === 0 ? (
              <p className="mt-3 text-sm text-red-600">No hay cuentas en {form.moneda || "PYG"} para esta empresa. Creá una en Bancos.</p>
            ) : (
              <div className="mt-3">
                <label className="text-xs text-gray-500 block mb-1">Cuenta donde se acredita {medioCobroSel.procesadora_id && procesadorasCobro.find(p => p.id === medioCobroSel.procesadora_id)?.cuenta_id ? "(la procesadora acredita en su cuenta)" : ""}</label>
                <select value={cuentaCobroSel} onChange={(e) => setCuentaCobroSel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white">
                  <option value="">— Seleccioná una cuenta —</option>
                  {cuentasParaCobro.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda || "PYG"})</option>)}
                </select>
              </div>
            )
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setCobroPicker(false)} className="px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Cancelar</button>
            <button type="button"
              disabled={cobroAEleccion && !cuentaCobroSel && !procesadorasCobro.find(p => p.id === medioCobroSel.procesadora_id)?.cuenta_id}
              onClick={() => { cuentaCobroRef.current = cuentaCobroSel; medioCobroRef.current = medioCobroSel; cobroConfirmadoRef.current = true; setCobroPicker(false); handleSave(); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50">
              Confirmar y guardar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FacturaFormModal;
